import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePlotsStore } from '@/store/plots';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { Eye, Code, Download, Share2, LineChart, ChartLine, Database, Save, Copy, Loader2, Check, FileImage } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
// Type fallback for react-plotly.js
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// Type fallback for react-plotly.js
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Plot from 'react-plotly.js';
import { withPlotTypography, plotlyTypographyDefaults } from '@/theme/plotlyTypography';
import { journalTemplatePresets } from '@/lib/journalTemplates';
import { mmToPx } from '@/lib/applyJournalTemplate';
import DataTab from './data-tab';
import CodeTab from './code-tab';
import { authorizedFetch } from '@/lib/queryClient';
import { usePlotlyCode } from '@/hooks/use-plotly-code';
import { clearDraft, computeFigureHash } from '@/lib/drafts/localDraft';
import { idbDeleteDraft, makeDraftKey } from '@/lib/drafts/indexedDbDraft';
import { track } from '@/analytics/mixpanel';
import { generateHueLightnessPalette } from '@/lib/colors/hueLightness';
import { getMapboxAccessToken } from '@/lib/plotly-mapbox-config';
// import { ensureContrast } from '@/lib/colors/contrast';
// import { assignNewDomainValues } from '@/lib/colors/mapping';

// Extend window interface for Plotly
declare global {
  interface Window {
    Plotly: any;
  }
}

type BracketInfo = {
  x0: number;
  x1: number;
  yTop: number;
  yLow: number;
  height: number;
  xCenter: number;
  raw?: {
    leftTop: number;
    rightTop: number;
    leftBottom: number;
    rightBottom: number;
  };
};

const parseBracketPath = (pathStr: string | undefined): BracketInfo | null => {
  if (!pathStr || typeof pathStr !== 'string') return null;
  const nums = (pathStr.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(parseFloat);
  if (nums.length < 8) return null;

  const xs = [nums[0], nums[2], nums[4], nums[6]].filter((v) => Number.isFinite(v));
  const ys = [nums[1], nums[3], nums[5], nums[7]].filter((v) => Number.isFinite(v));
  if (xs.length < 2 || ys.length < 2) return null;

  const normalizedX0 = Math.min(...xs);
  const normalizedX1 = Math.max(...xs);
  const normalizedYLow = Math.min(...ys);
  const normalizedYTop = Math.max(...ys);

  return {
    x0: normalizedX0,
    x1: normalizedX1,
    yLow: normalizedYLow,
    yTop: normalizedYTop,
    height: normalizedYTop - normalizedYLow,
    xCenter: (normalizedX0 + normalizedX1) / 2,
    raw: {
      leftBottom: nums[1],
      leftTop: nums[3],
      rightTop: nums[5],
      rightBottom: nums[7]
    }
  };
};

const formatBracketPath = (info: BracketInfo) =>
  `M ${info.x0},${info.yLow} L ${info.x0},${info.yTop} L ${info.x1},${info.yTop} L ${info.x1},${info.yLow}`;

const getAxisSpan = (layout: any, axisRef: string) => {
  if (!layout || axisRef === 'paper') return 1;
  const axisKey = axisRef.endsWith('axis') ? axisRef : `${axisRef}axis`;
  const axis = layout[axisKey];
  if (Array.isArray(axis?.range)) {
    const span = Math.abs(axis.range[1] - axis.range[0]);
    return span || 1;
  }
  return 1;
};

const findBracketLabelIndex = (layout: any, shape: any, info: BracketInfo): number | null => {
  const annotations: any[] = Array.isArray(layout?.annotations) ? layout.annotations : [];
  if (!annotations.length) return null;

  const xref = shape?.xref || 'paper';
  const yref = shape?.yref || 'paper';
  const yOffset = yref === 'paper' ? 0.02 : 0.02 * getAxisSpan(layout, yref);
  const expectedY = yref === 'paper' ? Math.min(0.98, info.yTop + yOffset) : info.yTop + yOffset;

  let bestIdx: number | null = null;
  let bestScore = Infinity;

  annotations.forEach((anno, idx) => {
    if (!anno || anno.showarrow) return;
    if ((anno.xref || 'paper') !== xref) return;
    if ((anno.yref || 'paper') !== yref) return;
    const dx = Math.abs((anno.x ?? 0) - info.xCenter);
    const dy = Math.abs((anno.y ?? 0) - expectedY);
    const score = dx + dy * 1.5;
    if (score < bestScore) {
      bestScore = score;
      bestIdx = idx;
    }
  });

  return bestIdx;
};

const clamp01 = (val: number) => Math.max(0, Math.min(1, val));

const syncBracketShapeAndLabel = (layout: any, shape: any) => {
  if (!shape || shape.type !== 'path') return;
  const info = parseBracketPath(shape.path);
  if (!info) return;

  const prevMeta = shape.lumiBracket || {};
  const prevTop = typeof prevMeta.yTop === 'number' ? prevMeta.yTop : info.yTop;
  const prevBottom = typeof prevMeta.yLow === 'number' ? prevMeta.yLow : info.yLow;

  const rawTopCandidates = info.raw ? [info.raw.leftTop, info.raw.rightTop].filter((v) => typeof v === 'number' && Number.isFinite(v)) : [];
  const rawBottomCandidates = info.raw ? [info.raw.leftBottom, info.raw.rightBottom].filter((v) => typeof v === 'number' && Number.isFinite(v)) : [];

  const pickAdjustedValue = (candidates: number[], previous: number) => {
    if (!candidates.length) return previous;
    const tolerance = 1e-6;
    const deltas = candidates.map((value) => ({ value, delta: Math.abs(value - previous) }));
    deltas.sort((a, b) => b.delta - a.delta);
    return deltas[0].delta < tolerance ? previous : deltas[0].value;
  };

  let newTop = clamp01(pickAdjustedValue(rawTopCandidates, prevTop));
  let newBottom = clamp01(pickAdjustedValue(rawBottomCandidates, prevBottom));
  if (newTop < newBottom) {
    const mid = (newTop + newBottom) / 2;
    newTop = clamp01(mid);
    newBottom = clamp01(mid);
  }

  const normalized: BracketInfo = {
    x0: Math.min(info.x0, info.x1),
    x1: Math.max(info.x0, info.x1),
    yLow: newBottom,
    yTop: newTop,
    height: Math.max(newTop - newBottom, 0),
    xCenter: (Math.min(info.x0, info.x1) + Math.max(info.x0, info.x1)) / 2
  };

  shape.path = formatBracketPath(normalized);
  shape.lumiBracket = {
    x0: normalized.x0,
    x1: normalized.x1,
    yTop: normalized.yTop,
    yLow: normalized.yLow
  };

  const labelIdx = findBracketLabelIndex(layout, shape, normalized);
  if (labelIdx !== null && layout?.annotations?.[labelIdx]) {
    const yref = shape?.yref || 'paper';
    const yOffset = yref === 'paper' ? 0.02 : 0.02 * getAxisSpan(layout, yref);
    const newY = yref === 'paper' ? Math.min(0.98, normalized.yTop + yOffset) : normalized.yTop + yOffset;
    layout.annotations[labelIdx] = {
      ...layout.annotations[labelIdx],
      x: normalized.xCenter,
      y: newY
    };
  }
};

export default function PlotCanvas({ unsaved, unsavedLabel, onAddToFigure, isDirty, onSave }: { unsaved?: boolean; unsavedLabel?: string; onAddToFigure?: () => void; isDirty?: boolean; onSave?: () => Promise<void> }) {
  const { 
    currentPlot,
    setCurrentPlot, 
    // selectedPoint, 
    setSelectedPoint,
    setSelectedSeries,
    selectPoint,
    selectSeries,
    clearSelection,
    selectLegendItem,
    selectAxis,
    selectTitle,
    withSnapshotGuard,
    markUserEdited
  } = usePlotsStore();
  const [selectionRev] = useState(0);
  const [activeTab, setActiveTab] = useState<'plot' | 'data' | 'code'>('plot');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  // Remove local dirty; rely on prop `unsaved` from parent/editor store
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const saveInProgressRef = useRef(false);
  const [shareToken, setShareToken] = useState('');
  const [conflict, setConflict] = useState<{ etag?: string; updatedAt?: string } | null>(null);
  const { toast } = useToast();
  // const plotRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphDivRef = useRef<any>(null);
  // const clearTimeoutRef = useRef<NodeJS.Timeout>();
  const [autoScale, setAutoScale] = useState<number>(1);
  const datasetInfoRef = useRef<{ name?: string; fileId?: string } | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const copiedCodeTimeoutRef = useRef<number | null>(null);
  const copiedShareTimeoutRef = useRef<number | null>(null);
  const [isCodeMissing, setIsCodeMissing] = useState(false); // Track if code is missing for drafts
  const prevIsCodeMissingRef = useRef<boolean>(false); // Track previous state to detect transitions
  const [showFirstSaveNote, setShowFirstSaveNote] = useState(false); // Track if we should show the first save note
  const firstSaveNoteTimeoutRef = useRef<number | null>(null); // Timeout ref for auto-dismissing the note

  // Always fetch code in background (even when Code tab is not active)
  // This ensures currentPlot.pythonCode is populated so isCodeMissing can clear
  // The hook result is passed to CodeTab to avoid duplicate fetches
  const codeHook = usePlotlyCode('python'); // Side effect: populates currentPlot.pythonCode via setCurrentPlotArtifacts
  
  // Telemetry: Log when code hydration starts (for debugging)
  useEffect(() => {
    const plotId = (currentPlot as any)?.id;
    const hasCode = (currentPlot as any)?.pythonCode && typeof (currentPlot as any)?.pythonCode === 'string' && (currentPlot as any).pythonCode.trim().length > 0;
    if (plotId && hasCode) {
      try { console.log('[plot-canvas] Code hydrated in background - Save button should be enabled'); } catch {}
    }
  }, [(currentPlot as any)?.id, (currentPlot as any)?.pythonCode]);

  // Listen for data analysis events to capture dataset name and fileId
  useEffect(() => {
    const onAnalyzed = (e: any) => {
      try {
        const det = e?.detail || {};
        if (det?.filename || det?.fileId) {
          datasetInfoRef.current = { name: det.filename, fileId: det.fileId };
          // Persist per-project for fallback
          const pid = (() => { try { const m = window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/); return m?.[1]; } catch { return undefined; } })();
          if (pid && det?.filename) localStorage.setItem(`lumiplot:project:${pid}:lastDatasetName`, String(det.filename));
          if (pid && det?.fileId) localStorage.setItem(`lumiplot:project:${pid}:lastDatasetFileId`, String(det.fileId));
        }
      } catch {}
    };
    window.addEventListener('lumi:dataAnalyzed', onAnalyzed as any);
    return () => window.removeEventListener('lumi:dataAnalyzed', onAnalyzed as any);
  }, []);

  // Monitor code availability for drafts - show tooltip/disable button if code is missing
  // Watch currentPlot.pythonCode directly to update immediately when code becomes available
  const pythonCode = (currentPlot as any)?.pythonCode;
  useEffect(() => {
    const draftId = (() => {
      try {
        const pid = (window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/)?.[1]) || (currentPlot as any)?.projectId;
        return pid ? (localStorage.getItem(`lumiplot:project:${pid}:lastDraftId`) || (window as any).__lumiplotLastDraftId || undefined) : ((window as any).__lumiplotLastDraftId || undefined);
      } catch { return (window as any).__lumiplotLastDraftId || undefined; }
    })();
    const plotId = (currentPlot as any)?.id;
    const isDraft = draftId === plotId || !(currentPlot as any)?.latestVersionId;
    
    // Only check for drafts
    if (isDraft && plotId) {
      const hasCode = pythonCode && typeof pythonCode === 'string' && pythonCode.trim().length > 0;
      const wasMissing = prevIsCodeMissingRef.current;
      const newIsMissing = !hasCode;
      setIsCodeMissing(newIsMissing);
      prevIsCodeMissingRef.current = newIsMissing;
      
      // Log state change for debugging (when code becomes available)
      if (hasCode && wasMissing) {
        try { console.log('[plot-canvas] Code became available - enabling Save button'); } catch {}
      }
    } else {
      // Not a draft - clear missing code state
      setIsCodeMissing(false);
      prevIsCodeMissingRef.current = false;
    }
  }, [currentPlot, pythonCode]); // Watch currentPlot and pythonCode to update immediately when code becomes available

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (firstSaveNoteTimeoutRef.current) {
        window.clearTimeout(firstSaveNoteTimeoutRef.current);
      }
    };
  }, []);

  // Debug: Log when showFirstSaveNote changes
  useEffect(() => {
    try { console.log('[plot-canvas] showFirstSaveNote state changed:', showFirstSaveNote); } catch {}
  }, [showFirstSaveNote]);

  // Handle save functionality (atomic snapshot with concurrency via If-Match)
  const handleSave = async () => {
    if (!currentPlot || isSaving || saveInProgressRef.current) return;

    setIsSaving(true);
    saveInProgressRef.current = true;
    try {
      let plotId = (currentPlot as any)?.id;
      const plotlyJson = (currentPlot as any)?.plotlyJson;
      if (!plotId || !plotlyJson) throw new Error('Missing plot');
      
      // Validate that plotlyJson has renderable data before saving
      const data = Array.isArray(plotlyJson?.data) ? plotlyJson.data : [];
      const hasRenderableData = data.length > 0 && data.some((trace: any) => {
        if (!trace) return false;
        const traceType = String(trace.type || '').toLowerCase();
        
        // For scatter-type traces (scatter, scattergl), require BOTH x AND y
        if (traceType === 'scatter' || traceType === 'scattergl') {
          return (Array.isArray(trace.x) && trace.x.length > 0) && (Array.isArray(trace.y) && trace.y.length > 0);
        }
        
        // Scattergeo/scattermapbox plots use lon (longitude) and lat (latitude) instead of x/y
        if (traceType === 'scattergeo' || traceType === 'scattermapbox') {
          return (Array.isArray(trace.lon) && trace.lon.length > 0) && (Array.isArray(trace.lat) && trace.lat.length > 0);
        }
        
        // Check if trace has any visible data arrays (for other trace types)
        return (
          (Array.isArray(trace.x) && trace.x.length > 0) ||
          (Array.isArray(trace.y) && trace.y.length > 0) ||
          (Array.isArray(trace.z) && trace.z.length > 0) ||
          (Array.isArray(trace.values) && trace.values.length > 0) ||
          (Array.isArray(trace.labels) && trace.labels.length > 0) ||
          (trace.type === 'pie' && Array.isArray(trace.values) && trace.values.length > 0) ||
          // Radar/polar plots use r (radius) and theta (angle) instead of x/y
          (Array.isArray(trace.r) && trace.r.length > 0 && Array.isArray(trace.theta) && trace.theta.length > 0) ||
          // Sankey plots use node.label and link.value (nested structure)
          ((Array.isArray(trace.node?.label) && trace.node.label.length > 0 || (Array.isArray(trace.node?.labels) && trace.node.labels.length > 0)) && Array.isArray(trace.link?.value) && trace.link.value.length > 0) ||
          // Indicator/gauge/bullet charts use a scalar value property (not arrays)
          ((trace.type === 'indicator' || trace.mode === 'gauge' || trace.mode === 'number') && typeof trace.value === 'number' && !isNaN(trace.value) && isFinite(trace.value))
        );
      });
      
      if (!hasRenderableData) {
        try { console.warn('[handleSave] Plot has no renderable data, cannot save', { dataLength: data.length, plotlyJson }); } catch {}
        toast({ 
          title: 'Cannot save plot', 
          description: 'The plot has no data. Please ensure the plot is rendered before saving.', 
          variant: 'destructive',
          force: true
        });
        setIsSaving(false);
        return;
      }

      // Create a preview PNG (data URL)
      // STEP 1: Instrument preview generation with logging and force toast
      let previewPng: string | undefined;
      let previewError: any = null;
      try {
        // STEP 2: Defensive check - ensure Plot tab is active and element exists
        const plotElement = document.querySelector('.js-plotly-plot') as any;
        
        if (!plotElement) {
          // Element not found - try to ensure Plot tab is active
          try {
            // Check if we're on a different tab by looking at activeTab state or button classes
            const plotTab = document.querySelector('[data-testid="button-plot-view"]');
            const isPlotTabActive = plotTab?.classList.contains('bg-secondary') || 
                                   plotTab?.getAttribute('data-active') === 'true' ||
                                   activeTab === 'plot';
            
            if (!isPlotTabActive && plotTab) {
              // Plot tab is not active - switch to it and wait
              try { console.log('[handleSave] Switching to Plot tab to generate preview'); } catch {}
              (plotTab as HTMLElement).click();
              await new Promise(resolve => setTimeout(resolve, 500)); // Wait for tab switch and render
            }
          } catch {}
          
          // Retry after potential tab switch
          const retryElement = document.querySelector('.js-plotly-plot') as any;
          if (!retryElement) {
            throw new Error('Plot element (.js-plotly-plot) not found. Please ensure the Plot tab is active and the chart is rendered before saving.');
          }
          
          // Use retry element
          if (retryElement && window.Plotly && window.Plotly.toImage) {
          // Use client size for preview to avoid fractional DPI artifacts
            let outWidth = (retryElement.clientWidth as number) || (retryElement.layout as any)?.width || 800;
            let outHeight = (retryElement.clientHeight as number) || (retryElement.layout as any)?.height || 600;
            previewPng = await window.Plotly.toImage(retryElement, { format: 'png', width: outWidth, height: outHeight, scale: 1 });
          }
        } else if (plotElement && window.Plotly && window.Plotly.toImage) {
          // Use client size for preview to avoid fractional DPI artifacts
          let outWidth = (plotElement.clientWidth as number) || (plotElement.layout as any)?.width || 800;
          let outHeight = (plotElement.clientHeight as number) || (plotElement.layout as any)?.height || 600;
          previewPng = await window.Plotly.toImage(plotElement, { format: 'png', width: outWidth, height: outHeight, scale: 1 });
        } else {
          throw new Error('Plotly.toImage not available or plot element invalid');
        }
        
        // STEP 1: Log preview generation success
        if (previewPng) {
          const previewLength = previewPng.length;
          try { console.log('[handleSave] Preview PNG generated successfully', { length: previewLength, isDataUrl: previewPng.startsWith('data:image') }); } catch {}
        } else {
          throw new Error('Preview PNG generation returned empty/null');
        }
      } catch (e) {
        previewError = e;
        const errorMsg = String((e as any)?.message || String(e) || 'Unknown error');
        
        // STEP 1: Log preview generation failure with details
        try { 
          console.error('[handleSave] Preview PNG generation failed', { 
            error: errorMsg, 
            errorType: (e as any)?.name || 'Error',
            plotElementExists: !!document.querySelector('.js-plotly-plot'),
            plotlyAvailable: !!(window.Plotly && window.Plotly.toImage),
            activeTab: document.querySelector('[data-testid="button-plot-view"]')?.classList.contains('bg-secondary')
          }); 
        } catch {}
        
        // STEP 1: Surface failure with force: true so users see it on plot-editor route
        try { 
          toast({ 
            title: 'Preview unavailable', 
            description: 'Could not generate a thumbnail. You can still save, but the gallery preview may be missing.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          }); 
        } catch {}
        try { (window as any).metricsInc?.('preview_generation_failed', { place: 'plot-canvas', reason: errorMsg || 'toImage_error' }); } catch {}
      }
      
      // STEP 1: Log preview status before save
      if (!previewPng) {
        try { console.warn('[handleSave] Saving without preview PNG', { error: previewError?.message || 'Not generated' }); } catch {}
      }

      // Try to infer projectId from URL: /plot-editor/:projectId
      let inferredProjectId: string | undefined;
      try {
        const m = window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/);
        inferredProjectId = m?.[1];
      } catch {}

      // If this is a plan_* or non-UUID id, create a new plot first so snapshot can be saved
      const looksLikeUuid = (id: string) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.test(id);
      if (!looksLikeUuid(String(plotId)) || String(plotId).startsWith('plan_')) {
        if (!inferredProjectId) throw new Error('Missing project');
        const createResp = await authorizedFetch(`/api/projects/${inferredProjectId}/plots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: (currentPlot as any)?.title || 'Untitled Plot' })
        });
        if (!createResp.ok) {
          toast({ title: 'Save failed', description: 'Could not create plot to save snapshot.', variant: 'destructive' });
          setIsSaving(false);
          return;
        }
        const pj = await createResp.json();
        const newId = pj?.id || pj?.plotId || pj?.plot_id;
        if (!newId) {
          toast({ title: 'Save failed', description: 'Server did not return a new plot id.', variant: 'destructive' });
          setIsSaving(false);
          return;
        }
        // Update state and URL to the new plot id
        try { setCurrentPlot({ ...(currentPlot as any), id: newId } as any); } catch {}
        try {
          const url = new URL(window.location.href);
          const parts = url.pathname.split('/');
          if (parts[1] === 'plot-editor') {
            parts[3] = String(newId);
            const next = parts.join('/') + (url.search || '') + (url.hash || '');
            history.replaceState(null, '', next);
          }
        } catch {}
        plotId = newId;
      }

      // Attach dataset hints from prior analysis if available
      let datasetNameHint: string | undefined;
      let datasetFileIdHint: string | undefined;
      try {
        datasetNameHint = datasetInfoRef.current?.name;
        datasetFileIdHint = datasetInfoRef.current?.fileId;
        if (!datasetNameHint && inferredProjectId) datasetNameHint = localStorage.getItem(`lumiplot:project:${inferredProjectId}:lastDatasetName`) || undefined;
        if (!datasetFileIdHint && inferredProjectId) datasetFileIdHint = localStorage.getItem(`lumiplot:project:${inferredProjectId}:lastDatasetFileId`) || undefined;
      } catch {}

      // Build If-Match from stored concurrency token
      // IMPORTANT: Read from localStorage fresh each time to get the latest etag
      // This prevents race conditions where multiple save attempts use stale etags
      const etagKey = `lumiplot:plot:${plotId}:etag`;
      const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
      let ifMatch = localStorage.getItem(etagKey) || localStorage.getItem(updatedKey) || '';
      
      // Helper to refresh etag from localStorage (useful after 409 conflicts)
      const refreshEtag = () => {
        ifMatch = localStorage.getItem(etagKey) || localStorage.getItem(updatedKey) || '';
        return ifMatch;
      };

      // Helper to perform the POST with a specific If-Match token (optional)
      // Include draftId if available so server can copy stats.json from draft to committed version
      const draftId = (() => {
        try {
          const pid = inferredProjectId || (window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/)?.[1]);
          return pid ? (localStorage.getItem(`lumiplot:project:${pid}:lastDraftId`) || (window as any).__lumiplotLastDraftId || undefined) : ((window as any).__lumiplotLastDraftId || undefined);
        } catch { return (window as any).__lumiplotLastDraftId || undefined; }
      })();

      // Ensure Python code is available before saving
      // If currentPlot.pythonCode is missing, fetch it from the server
      // For drafts, wait for usePlotlyCode hook to populate code before fetching from server
      let pythonCode = (currentPlot as any)?.pythonCode;
      const isDraft = draftId === plotId || !(currentPlot as any)?.latestVersionId;
      
      if (!pythonCode || typeof pythonCode !== 'string' || pythonCode.trim().length === 0) {
        // For drafts: wait for usePlotlyCode hook to populate code (handles race condition)
        if (isDraft) {
          for (let attempt = 0; attempt < 3; attempt++) {
            // Wait progressively longer: 100ms, 300ms, 500ms
            if (attempt > 0) {
              await new Promise(resolve => setTimeout(resolve, 100 + (attempt * 200)));
            }
            
            // Re-check currentPlot.pythonCode (might have been updated by usePlotlyCode hook)
            try {
              const { usePlotsStore } = await import('@/store/plots');
              const updatedPlot = usePlotsStore.getState().currentPlot;
              const updatedCode = (updatedPlot as any)?.pythonCode;
              if (updatedCode && typeof updatedCode === 'string' && updatedCode.trim().length > 0) {
                pythonCode = updatedCode;
                break;
              }
            } catch {}
          }
        }
        
        // If still no code (or not a draft), try fetching from server
        if (!pythonCode || typeof pythonCode !== 'string' || pythonCode.trim().length === 0) {
          try {
            // Try to fetch from edited_script.py first, then script.py, then export-code
            let codeText: string | null = null;
            
            if (isDraft && inferredProjectId) {
              // Draft: fetch from draft artifacts first
              try {
                const r = await authorizedFetch(`/api/ai/v2/drafts/${inferredProjectId}/${plotId}/edited_script.py`);
                if (r.ok) codeText = await r.text();
              } catch {
                try {
                  const r2 = await authorizedFetch(`/api/ai/v2/drafts/${inferredProjectId}/${plotId}/script.py`);
                  if (r2.ok) codeText = await r2.text();
                } catch {}
              }
            } else {
              // Saved plot: fetch from versions
              try {
                const r = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/edited_script.py`);
                if (r.ok) codeText = await r.text();
              } catch {
                try {
                  const r2 = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/script.py`);
                  if (r2.ok) codeText = await r2.text();
                } catch {}
              }
            }
            
            // Last resort: export-code using current figure (works for both drafts and saved plots)
            if (!codeText && inferredProjectId) {
              try {
                const exportResp = await authorizedFetch(`/api/ai/v2/export-code`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ 
                    plotId, 
                    currentPlotlyJson: plotlyJson, 
                    format: 'python', 
                    projectId: inferredProjectId,
                    claimToken: ((): string | null => { try { return (window as any).__lumiplotClaims?.[String(plotId)] || null; } catch { return null; } })() || (currentPlot as any)?.claimToken || undefined
                  })
                });
                if (exportResp.ok) {
                  const exportData = await exportResp.json();
                  codeText = exportData?.code || null;
                }
              } catch {}
            }
            
            if (codeText && codeText.trim().length > 0) {
              pythonCode = codeText;
              // Update the store so future saves reuse this code
              try {
                const { usePlotsStore } = await import('@/store/plots');
                usePlotsStore.getState().setCurrentPlotArtifacts?.({ id: plotId, pythonCode: codeText } as any);
              } catch {}
            }
          } catch (fetchErr) {
            // Log the error for debugging
            try { console.warn('[handleSave] Failed to fetch Python code:', fetchErr); } catch {}
          }
        }
      }

      // STEP 1: Abort save if code is still missing (for drafts)
      // API contract requires scripts to be present, so we must block save if code is missing
      if (isDraft && (!pythonCode || typeof pythonCode !== 'string' || pythonCode.trim().length === 0)) {
        try { console.warn('[handleSave] Aborting save: Python code is required but missing for draft'); } catch {}
        setIsCodeMissing(true); // Set state to show banner/disable button
        toast({ 
          title: 'Save blocked', 
          description: 'Python script is required but could not be found. Please wait for code generation to complete or reload the page before saving.', 
          variant: 'destructive',
          force: true // Force toast to show on plot-editor route
        });
        // Log toast call for debugging (Step 3)
        try { console.log('[handleSave] Toast shown: Save blocked - missing Python code'); } catch {}
        return;
      }
      
      // Clear missing code state if code is available
      setIsCodeMissing(false);

      const doPost = async (token?: string) => {
        try {
          // Use provided token, or refresh etag from localStorage, or use current ifMatch
          const etagToUse = token || refreshEtag() || ifMatch;
          return await authorizedFetch(`/api/ai/v2/plots/${plotId}/save-snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(etagToUse ? { 'If-Match': etagToUse } : {}) },
        body: JSON.stringify({
          currentPlotlyJson: plotlyJson,
          previewPng,
          projectId: inferredProjectId,
          datasetName: datasetNameHint,
          datasetFileId: datasetFileIdHint,
          draftId,
              scriptPy: pythonCode || undefined,
              editedScriptPy: pythonCode || undefined,
          statsJson: (currentPlot as any)?.statsJson || undefined,
          insights: (() => {
            // CRITICAL: Validate that insights.plotId matches the plotId being saved
            const insights = (currentPlot as any)?.insights;
            if (!insights) return undefined;
            
            const insightsPlotId = insights.plotId;
            const currentPlotId = (currentPlot as any)?.id;
            
            // Only include insights if plotId matches or is null/undefined (will be set by backend)
            if (insightsPlotId && insightsPlotId !== plotId && insightsPlotId !== currentPlotId) {
              try { 
                console.warn('[plot-canvas] insights plotId mismatch, not saving insights', { 
                  insightsPlotId, 
                  savePlotId: plotId,
                  currentPlotId 
                }); 
              } catch {}
              return undefined; // Don't save mismatched insights
            }
            
            // Ensure plotId is set correctly before saving
            if (insights.schemaVersion === 'InsightV2') {
              insights.plotId = plotId;
              insights.projectId = inferredProjectId;
            }
            
            try {
              console.info('[plot-canvas] save payload insights', { 
                hasInsights: !!insights, 
                blocksCount: insights?.blocks?.length || 0, 
                schemaVersion: insights?.schemaVersion,
                plotId: insights?.plotId,
                savePlotId: plotId
              });
            } catch {}
            
            return insights;
          })(),
          // Debug: log insights being saved
          _debugInsights: (() => {
            try {
              const insights = (currentPlot as any)?.insights;
              console.info('[plot-canvas] save payload insights debug', { 
                hasInsights: !!insights, 
                blocksCount: insights?.blocks?.length || 0, 
                schemaVersion: insights?.schemaVersion,
                insightsPlotId: insights?.plotId,
                currentPlotId: (currentPlot as any)?.id,
                savePlotId: plotId
              });
            } catch {}
            return undefined;
          })(),
          titleHint: (currentPlot as any)?.title || undefined,
        })
      });
        } catch (networkErr) {
          // CRITICAL FIX: authorizedFetch throws errors for non-ok responses (including 409)
          // We need to catch 409 errors and return a Response object so the 409 handler can process it
          const errMsg = String(networkErr);
          if (errMsg.includes('HTTP 409') || errMsg.includes('409:')) {
            // Extract the response body from the error message
            try {
              const jsonMatch = errMsg.match(/\{.*\}/);
              if (jsonMatch) {
                const conflictBody = JSON.parse(jsonMatch[0]);
                // Create a mock Response object with status 409 and the parsed body
                // This allows the 409 handler below to process it correctly
                const mockResponse = new Response(JSON.stringify(conflictBody), {
                  status: 409,
                  statusText: 'Conflict',
                  headers: { 'Content-Type': 'application/json' }
                });
                // Mark that we've already parsed the body
                (mockResponse as any)._parsedBody = conflictBody;
                return mockResponse;
              }
            } catch (parseErr) {
              try { console.warn('[handleSave] Failed to parse 409 error body:', parseErr); } catch {}
            }
          }
          // Network errors (e.g., fetch failed, timeout) should be retried
          throw new Error(`NETWORK_ERROR: ${String(networkErr)}`);
        }
      };

      // Retry mechanism for save operation (handles network errors, 5xx, etc.)
      let resp: Response | null = null;
      let lastError: Error | null = null;
      const maxRetries = 3;
      
      for (let retryAttempt = 0; retryAttempt < maxRetries; retryAttempt++) {
        try {
          // Wait before retry (except first attempt)
          if (retryAttempt > 0) {
            await new Promise(resolve => setTimeout(resolve, 500 * retryAttempt));
          }
          
          resp = await doPost();
          lastError = null;
          
          // If we got a response, break out of retry loop (even if error status)
          // We'll handle error statuses below
          break;
        } catch (error) {
          lastError = error as Error;
          const errorMsg = String((error as any)?.message || String(error));
          
          // Check if this is a network error (retryable)
          const isNetworkError = errorMsg.includes('NETWORK_ERROR') || 
                               errorMsg.includes('fetch') || 
                               errorMsg.includes('network') || 
                               errorMsg.includes('Failed to fetch') ||
                               (error as any)?.name === 'TypeError';
          
          // Only retry network errors
          if (isNetworkError && retryAttempt < maxRetries - 1) {
            // Continue retry loop
            continue;
          }
          
          // Not a network error or last attempt - throw to be caught by outer catch
          throw error;
        }
      }
      
      // If we exhausted retries and still have an error, throw it
      if (lastError && !resp) {
        throw lastError;
      }
      
      // If resp is still null (shouldn't happen, but safety check)
      if (!resp) {
        throw new Error('Save failed: no response received after retries');
      }

      // Handle different response statuses
      if (resp.status === 413) {
        toast({ 
          title: 'Save failed', 
          description: 'Figure too large to save.', 
          variant: 'destructive',
          force: true // Force toast to show on plot-editor route
        });
        // Log toast call for debugging (Step 3)
        try { console.log('[handleSave] Toast shown: Figure too large (413)'); } catch {}
        return;
      }

      // Handle 5xx server errors
      if (resp.status >= 500) {
        const errorText = await resp.text().catch(() => 'Server error');
        toast({ 
          title: 'Save failed', 
          description: `Server error (${resp.status}). Please try again in a moment.`, 
          variant: 'destructive',
          force: true // Force toast to show on plot-editor route
        });
        // Log toast call for debugging (Step 3)
        try { console.log(`[handleSave] Toast shown: Server error (${resp.status})`); } catch {}
        throw new Error(`SERVER_ERROR_${resp.status}: ${errorText}`);
      }

      // Handle 4xx client errors (except 409 which is handled separately)
      if (resp.status >= 400 && resp.status < 500 && resp.status !== 409) {
        let errorBody: any = {};
        try {
          errorBody = await resp.json();
        } catch {
          errorBody = { error: `HTTP ${resp.status}` };
        }
        const errorMsg = String(errorBody?.error || errorBody?.message || `Save failed with status ${resp.status}`);
        toast({ 
          title: 'Save failed', 
          description: errorMsg,
          variant: 'destructive',
          force: true // Force toast to show on plot-editor route
        });
        // Log toast call for debugging (Step 3)
        try { console.log(`[handleSave] Toast shown: Client error (${resp.status}) - ${errorMsg}`); } catch {}
        throw new Error(`CLIENT_ERROR_${resp.status}: ${errorMsg}`);
      }

      if (resp.status === 409) {
        // Check if this is a SCRIPT_MISSING error (not a concurrency conflict)
        // CRITICAL: If response body was already parsed (from authorizedFetch error), use it directly
        const conflictBody = (resp as any)._parsedBody || await resp.json().catch(() => ({}));
        try { console.log(`[handleSave] 🔴 409 Conflict received:`, { hasEtag: !!conflictBody?.etag, hasUpdatedAt: !!conflictBody?.updatedAt, error: conflictBody?.error, code: conflictBody?.code }); } catch {}
        
        // IMPORTANT: Update localStorage immediately for CONFLICT errors to prevent race conditions
        // This ensures that if another save attempt happens in parallel, it will use the updated etag
        if (conflictBody?.etag || conflictBody?.updatedAt) {
          try {
            const etagKey = `lumiplot:plot:${plotId}:etag`;
            const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
            const oldEtag = localStorage.getItem(etagKey);
            if (conflictBody?.etag) {
              const newEtag = String(conflictBody.etag);
              localStorage.setItem(etagKey, newEtag);
              ifMatch = newEtag; // Update local variable immediately
              try { console.log(`[handleSave] 🔄 409: Updated etag from ${oldEtag?.substring(0, 8) || 'none'}... to ${newEtag.substring(0, 8)}...`); } catch {}
            }
            if (conflictBody?.updatedAt) localStorage.setItem(updatedKey, String(conflictBody.updatedAt));
          } catch (updateErr) {
            try { console.error(`[handleSave] ❌ Failed to update localStorage on 409:`, updateErr); } catch {}
          }
        } else {
          try { console.warn(`[handleSave] ⚠️ 409 response missing etag! Body:`, conflictBody); } catch {}
        }
        
        if (conflictBody?.code === 'SCRIPT_MISSING' || conflictBody?.error === 'SCRIPT_MISSING') {
          // STEP 2: Enhance 409 recovery - proactively invoke export-code before retrying
          // For drafts: call export-code immediately to cut latency, then retry with progressive delays
          if (isDraft) {
            let retrySuccess = false;
            let retryCode: string | null = null;
            
            // STEP 2 (Optimized): Call export-code FIRST to generate code immediately (before retries)
            // This reduces latency by starting code generation as soon as we detect SCRIPT_MISSING
            if (!retryCode && inferredProjectId) {
              try {
                try { console.log('[handleSave] Calling export-code immediately to generate missing Python script (409 recovery)'); } catch {}
                const exportResp = await authorizedFetch(`/api/ai/v2/export-code`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ 
                    plotId, 
                    currentPlotlyJson: plotlyJson, 
                    format: 'python', 
                    projectId: inferredProjectId,
                    claimToken: ((): string | null => { try { return (window as any).__lumiplotClaims?.[String(plotId)] || null; } catch { return null; } })() || (currentPlot as any)?.claimToken || undefined
                  })
                });
                if (exportResp.ok) {
                  const exportData = await exportResp.json();
                  const exportCode = exportData?.code || null;
                  if (exportCode && typeof exportCode === 'string' && exportCode.trim().length > 0) {
                    retryCode = exportCode;
                    // Update store with generated code
                    try {
                      const { usePlotsStore } = await import('@/store/plots');
                      usePlotsStore.getState().setCurrentPlotArtifacts?.({ id: plotId, pythonCode: exportCode } as any);
                    } catch {}
                    try { console.log('[handleSave] Export-code generated Python script successfully (409 recovery)'); } catch {}
                  }
                }
              } catch (exportErr) {
                try { console.warn('[handleSave] Export-code failed (409 recovery):', exportErr); } catch {}
              }
            }
            
            // Retry loop with progressive delays (checking store and draft as fallback)
            for (let retryAttempt = 0; retryAttempt < 3; retryAttempt++) {
              // Wait progressively longer: 200ms, 400ms, 600ms (only if we don't already have code from export-code)
              if (retryAttempt > 0 && !retryCode) {
                await new Promise(resolve => setTimeout(resolve, 200 + (retryAttempt * 200)));
              }
              
              // Re-check if code is now available from usePlotlyCode hook
              if (!retryCode) {
                try {
                  const { usePlotsStore } = await import('@/store/plots');
                  const updatedPlot = usePlotsStore.getState().currentPlot;
                  const updatedCode = (updatedPlot as any)?.pythonCode;
                  if (updatedCode && typeof updatedCode === 'string' && updatedCode.trim().length > 0) {
                    retryCode = updatedCode;
                  }
                } catch {}
              }
              
              // Try fetching from draft if still no code
              if (!retryCode && inferredProjectId) {
                try {
                  const draftCodeResp = await authorizedFetch(`/api/ai/v2/drafts/${inferredProjectId}/${plotId}/edited_script.py`);
                  if (draftCodeResp.ok) {
                    const draftCode = await draftCodeResp.text();
                    if (draftCode && draftCode.trim().length > 0) {
                      retryCode = draftCode;
                    }
                  }
                } catch {}
                
                // If draft fetch failed, try script.py
                if (!retryCode) {
                  try {
                    const draftCodeResp2 = await authorizedFetch(`/api/ai/v2/drafts/${inferredProjectId}/${plotId}/script.py`);
                    if (draftCodeResp2.ok) {
                      const draftCode2 = await draftCodeResp2.text();
                      if (draftCode2 && draftCode2.trim().length > 0) {
                        retryCode = draftCode2;
                      }
                    }
                  } catch {}
                }
              }
              
              // If we found code, update pythonCode and retry save
              // Refresh etag before retry to ensure we use the latest value
              if (retryCode) {
                pythonCode = retryCode;
                refreshEtag(); // Refresh etag before retry
                resp = await doPost();
                if (resp.status === 200 || resp.status === 201) {
                  retrySuccess = true;
                  break;
                }
                // If retry failed with new code, clear it and try again
                retryCode = null;
              }
            }
            
            if (!retrySuccess) {
              setIsCodeMissing(true); // Set state to show banner/disable button
              toast({ 
                title: 'Save failed', 
                description: conflictBody?.message || 'Python script was not found after attempting to generate it. Please wait for code generation to complete or reload the page before saving.', 
                variant: 'destructive',
                force: true // Force toast to show on plot-editor route
              });
              // Log toast call for debugging (Step 3)
              try { console.log('[handleSave] Toast shown: Save failed - SCRIPT_MISSING after retries'); } catch {}
              return;
            }
          } else {
            // Not a draft - show error immediately
            toast({ 
              title: 'Save failed', 
              description: conflictBody?.message || 'Python script was not found. Please wait for code generation to complete or reload the page before saving.', 
              variant: 'destructive',
              force: true // Force toast to show on plot-editor route
            });
            // Log toast call for debugging (Step 3)
            try { console.log('[handleSave] Toast shown: Save failed - SCRIPT_MISSING (not draft)'); } catch {}
            return;
          }
        } else {
          // Regular concurrency conflict - handle as before
        const nextToken = String(conflictBody?.etag || conflictBody?.updatedAt || '');
        if (nextToken) {
          // Note: localStorage already updated above when we detected the 409
          // Retry once with the server-provided tag
          try {
            try { console.log(`[handleSave] Retrying save with updated etag: ${nextToken.substring(0, 8)}...`); } catch {}
            resp = await doPost(nextToken);
            try { console.log(`[handleSave] Retry response status: ${resp.status}`); } catch {}
            // Check if retry still resulted in conflict
            if (resp.status === 409) {
              // Still conflicting – update localStorage with latest etag and show banner
              const retryConflictBody = await resp.json().catch(() => ({}));
              if (retryConflictBody?.etag || retryConflictBody?.updatedAt) {
                try {
                  const etagKey = `lumiplot:plot:${plotId}:etag`;
                  const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
                  if (retryConflictBody?.etag) {
                    localStorage.setItem(etagKey, String(retryConflictBody.etag));
                    ifMatch = String(retryConflictBody.etag); // Update local variable
                  }
                  if (retryConflictBody?.updatedAt) localStorage.setItem(updatedKey, String(retryConflictBody.updatedAt));
                } catch {}
              }
              setConflict({ etag: retryConflictBody?.etag || conflictBody?.etag, updatedAt: retryConflictBody?.updatedAt || conflictBody?.updatedAt });
              toast({ 
                title: 'Concurrent save detected', 
                description: 'Another save operation is in progress. Please wait a moment and try again, or reload the latest version.', 
                variant: 'destructive',
                duration: 5000
              });
              return;
            }
            // If retry succeeded (not 409), continue to parse response below
            try { console.log(`[handleSave] ✅ Retry succeeded! Status: ${resp.status}, will parse response below`); } catch {}
          } catch (retryError) {
            // If retry itself failed with network error, treat as network error
            const retryErrorMsg = String((retryError as any)?.message || String(retryError));
            if (retryErrorMsg.includes('NETWORK_ERROR') || retryErrorMsg.includes('fetch') || retryErrorMsg.includes('network')) {
              toast({ 
                title: 'Save failed', 
                description: 'Network error occurred during retry. Please check your connection and try again.', 
                variant: 'destructive',
                force: true
              });
              return;
            }
            // Other errors - rethrow to be handled by outer catch
            throw retryError;
          }
        } else {
          // No token available - cannot retry, show conflict banner
          setConflict({ etag: conflictBody?.etag, updatedAt: conflictBody?.updatedAt });
          toast({ 
            title: 'Concurrent save detected', 
            description: 'Another save operation is in progress. Please wait a moment and try again, or reload the latest version.', 
            variant: 'destructive',
            duration: 5000
          });
          return;
        }
      }
      }

      // Parse response JSON (handle parse errors)
      // IMPORTANT: Response body can only be read once, so we must clone it if it was already read
      // However, if resp.status is 200, it shouldn't have been read yet (409 handler only reads on 409)
      let json: any = {};
      try {
        // Check if response body has already been consumed (for debugging)
        const bodyUsed = (resp as any).bodyUsed;
        if (bodyUsed) {
          try { console.warn(`[handleSave] ⚠️ Response body already consumed! Status: ${resp.status}`); } catch {}
        }
        json = await resp.json();
        try { console.log(`[handleSave] 📦 Parsed response:`, { success: json?.success, hasEtag: !!json?.etag, status: resp.status }); } catch {}
      } catch (parseErr) {
        // If JSON parsing fails, try to get text
        const errorText = await resp.text().catch(() => 'Invalid response');
        try { console.error(`[handleSave] ❌ Failed to parse response JSON:`, parseErr, 'Response text:', errorText.substring(0, 200)); } catch {}
        toast({ 
          title: 'Save failed', 
          description: `Server returned invalid response. Please try again.`, 
          variant: 'destructive' 
        });
        throw new Error(`INVALID_RESPONSE: ${errorText}`);
      }

      if ((json as any)?.success) {
        // Store concurrency tokens for next save
        if ((json as any).etag) {
          const oldEtag = localStorage.getItem(etagKey);
          localStorage.setItem(etagKey, String((json as any).etag));
          ifMatch = String((json as any).etag); // Update local variable
          try { console.log(`[handleSave] ✅ Success: Updated etag from ${oldEtag?.substring(0, 8) || 'none'}... to ${String((json as any).etag).substring(0, 8)}...`); } catch {}
        } else {
          try { console.warn(`[handleSave] ⚠️ Success response missing etag! Response:`, json); } catch {}
        }
        if ((json as any).updatedAt) localStorage.setItem(updatedKey, String((json as any).updatedAt));
        localStorage.setItem('lastPlotId', plotId);
        
        // Check if this was a draft (newly generated plot) being saved for the first time
        const versionId = (json as any)?.versionId;
        const wasDraft = !(currentPlot as any)?.latestVersionId;
        
        // CRITICAL: Update plots store with latestVersionId FIRST, before invalidating cache
        // This ensures panels that re-hydrate immediately after cache invalidation will see the new version
        // This prevents race conditions where panels try to resolve latestVersionId before it's updated
        if (versionId && plotId) {
          try {
            const { usePlotsStore } = await import('@/store/plots');
            const plotsState = usePlotsStore.getState();
            
            // Update currentPlot if it matches
            if (currentPlot && (currentPlot as any).id === plotId) {
              setCurrentPlot({ ...(currentPlot as any), latestVersionId: versionId } as any);
            }
            
            // Update plots array
            const existingPlot = plotsState.plots.find(p => p.id === plotId);
            if (existingPlot) {
              usePlotsStore.setState((prev) => ({
                plots: prev.plots.map(p => 
                  p.id === plotId 
                    ? { ...p, latestVersionId: versionId }
                    : p
                ),
              }));
            } else if (inferredProjectId) {
              // Plot not in array yet - add it with latestVersionId
              // This can happen if plot was created but not loaded into plots array
              usePlotsStore.setState((prev) => ({
                plots: [
                  ...prev.plots,
                  {
                    id: plotId,
                    projectId: inferredProjectId,
                    title: (currentPlot as any)?.title || 'Untitled Plot',
                    plotlyJson: (currentPlot as any)?.plotlyJson || { data: [], layout: {} },
                    pythonCode: (currentPlot as any)?.pythonCode ?? null,
                    createdAt: (currentPlot as any)?.createdAt || new Date(),
                    creditsConsumed: 0,
                    latestVersionId: versionId,
                  } as any,
                ],
              }));
            }
            
            // Update byProjectPlots cache if projectId is available
            if (inferredProjectId) {
              usePlotsStore.setState((prev) => {
                const cache = { ...(prev.byProjectPlots || {}) } as Record<string, Record<string, any>>;
                cache[inferredProjectId] = cache[inferredProjectId] || {};
                const existing = cache[inferredProjectId][plotId];
                cache[inferredProjectId][plotId] = {
                  ...(existing || {}),
                  id: plotId,
                  projectId: inferredProjectId,
                  latestVersionId: versionId,
                };
                return { byProjectPlots: cache } as any;
              });
            }
            
            console.log(`[handleSave] ✅ Updated plots store with latestVersionId: ${versionId} for plot ${plotId}`);
          } catch (updateErr) {
            console.warn('[handleSave] Failed to update plots store with latestVersionId:', updateErr);
          }
        }
        
        // CRITICAL: Invalidate figure editor panel cache AFTER updating plots store
        // This ensures panels that re-hydrate will see the updated latestVersionId
        // Preserve pinned versions so they don't unnecessarily refetch
        try {
          if (plotId) {
            const composition = useFigureCompositionStore.getState().currentComposition;
            const pinnedVersions = composition?.panels
              .filter(p => p.plotId === plotId && p.plotVersionId)
              .map(p => p.plotVersionId!)
              .filter((v): v is string => !!v) || [];
            useFigureCompositionStore.getState().invalidatePanelCacheForPlot(plotId, pinnedVersions.length > 0 ? pinnedVersions : undefined);
          }
        } catch (e) {
          try {
            console.warn('[handleSave] Failed to invalidate figure panel cache', e);
          } catch {}
        }
        
      // Notify editor page to update baseline & clear draft
        try {
          const evt = new CustomEvent('lumiplot-save-success', { detail: { plotId, projectId: inferredProjectId, figure: plotlyJson, etag: (json as any).etag, updatedAt: (json as any).updatedAt, versionId } });
          window.dispatchEvent(evt);
          // Trigger post-save CTA tour steps if available
          try {
            const showCTAs = (window as any).__lumiShowPostSaveCTAs;
            if (typeof showCTAs === 'function') {
              setTimeout(() => showCTAs(), 500); // Small delay to ensure UI is updated
            }
          } catch {}
        } catch {}
        // One-time: dispatch first-save beacon trigger and show note
        try {
          const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
          const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || 'anon';
          const firstKey = `${userId}:hasSavedOnce:v1`;
          const noteKey = `${userId}:showFirstSaveNote:v1`;
          
          if (!localStorage.getItem(firstKey)) {
            localStorage.setItem(firstKey, '1');
            window.dispatchEvent(new Event('lumiplot:first-plot-saved'));
          }
          
          // Show the note if this is a newly generated plot (draft) being saved for the first time
          // Show it every time a draft is saved (not checking localStorage - show once per save session)
          try { 
            console.log('[handleSave] First save note check:', JSON.stringify({ 
              wasDraft, 
              hasLatestVersionId: !!(currentPlot as any)?.latestVersionId,
              currentPlotId: (currentPlot as any)?.id,
              willShowNote: wasDraft
            })); 
          } catch {}
          
          if (wasDraft) {
            setShowFirstSaveNote(true);
            try { console.log('[handleSave] ✅ Showing first save note - state set to true'); } catch {}
            // Auto-dismiss the note after 10 seconds
            if (firstSaveNoteTimeoutRef.current) {
              window.clearTimeout(firstSaveNoteTimeoutRef.current);
            }
            // @ts-ignore setTimeout returns number in browsers
            firstSaveNoteTimeoutRef.current = window.setTimeout(() => {
              setShowFirstSaveNote(false);
            }, 10000);
          } else {
            try { 
              console.log('[handleSave] ❌ Not showing note - plot is not a draft (already has latestVersionId)'); 
            } catch {}
          }
        } catch {}
        // Clear local drafts (localStorage + IndexedDB) for this plot
        try { if (inferredProjectId) clearDraft(inferredProjectId, plotId); } catch {}
        try {
          const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
          const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
          const tenantId = (window as any).__lumiTenantId || null;
          const nsKey = makeDraftKey(tenantId, userId, inferredProjectId || null, plotId);
          await idbDeleteDraft(nsKey);
        } catch {}
        setConflict(null);
        setIsCodeMissing(false); // Clear missing code state on successful save
        toast({ 
          title: 'Saved to Gallery', 
          description: 'Your plot has been saved successfully and is now in the gallery.', 
          force: true // Force show on plot-editor route for better UX
        });
        // Log toast call for debugging (Step 3)
        try { console.log('[handleSave] Toast shown: Save successful'); } catch {}
        try {
          const pid = String(inferredProjectId || (currentPlot as any)?.projectId || '');
          const did = String(plotId);
          const runId = (() => {
            try { return localStorage.getItem(`lumiplot:lastRunId:${pid}:${did}`) || ''; } catch { return ''; }
          })();
          if (runId) {
            track('chart:saved', { chart_id: String(plotId), run_id: runId, name_customized: Boolean((currentPlot as any)?.title && (currentPlot as any).title !== 'Untitled Plot') });
          }
        } catch {}
      } else {
        // Server returned success: false
        const errCode = String((json as any)?.error || (json as any)?.code || 'Save failed');
        const errMsg = String((json as any)?.message || errCode);
        
        if (errCode === 'STATS_MISSING') {
          toast({ 
            title: 'Save blocked', 
            description: 'Stats were missing. Please generate analysis (stats) before saving.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          });
          // Log toast call for debugging (Step 3)
          try { console.log('[handleSave] Toast shown: STATS_MISSING'); } catch {}
          try { (window as any).metricsInc?.('save_snapshot_blocked_stats_missing', {}); } catch {}
        } else if (errCode === 'SCRIPT_MISSING') {
          setIsCodeMissing(true); // Set state to show banner/disable button
          toast({ 
            title: 'Save blocked', 
            description: 'Python script was not found. Please wait for code generation to complete or reload the page before saving.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          });
          // Log toast call for debugging (Step 3)
          try { console.log('[handleSave] Toast shown: SCRIPT_MISSING (from response body)'); } catch {}
        } else {
          // Generic error from server
          toast({ 
            title: 'Save failed', 
            description: errMsg || 'Server returned an error. Please try again.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          });
          // Log toast call for debugging (Step 3)
          try { console.log(`[handleSave] Toast shown: Generic error - ${errCode}`); } catch {}
        }
        throw new Error(errCode);
      }
    } catch (error) {
      // Ensure we always show a toast for any error
      const errorMsg = String((error as any)?.message || String(error) || 'Unknown error');
      
      // Check if toast was already shown (for specific error codes)
      const alreadyShown = errorMsg.includes('STATS_MISSING') || 
                          errorMsg.includes('SCRIPT_MISSING') || 
                          errorMsg.includes('CLIENT_ERROR_') ||
                          errorMsg.includes('SERVER_ERROR_') ||
                          errorMsg.includes('INVALID_RESPONSE') ||
                          errorMsg.includes('Figure too large');
      
      if (!alreadyShown) {
        // Determine error type for better messaging
        if (errorMsg.includes('NETWORK_ERROR') || errorMsg.includes('fetch') || errorMsg.includes('network')) {
          toast({ 
            title: 'Save failed', 
            description: 'Network error occurred. Please check your connection and try again.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          });
          // Log toast call for debugging (Step 3)
          try { console.log('[handleSave] Toast shown: Network error'); } catch {}
        } else if (errorMsg.includes('STATS_MISSING')) {
          // Telemetry only (toast already shown above)
          try { (window as any).metricsInc?.('save_snapshot_blocked_stats_missing', {}); } catch {}
        } else {
          // Generic error
          toast({ 
            title: 'Save failed', 
            description: 'Failed to save your changes. Please try again.', 
            variant: 'destructive',
            force: true // Force toast to show on plot-editor route
          });
          // Log toast call for debugging (Step 3)
          try { console.log(`[handleSave] Toast shown: Generic error in catch block - ${errorMsg.substring(0, 100)}`); } catch {}
        }
      }
      
      // Re-throw so finally block can clean up
      throw error;
    } finally {
      setIsSaving(false);
      saveInProgressRef.current = false;
    }
  };

  const reloadLatestFromServer = async () => {
    try {
      const plotId = (currentPlot as any)?.id;
      if (!plotId) return;
      const [figRes, metaRes] = await Promise.all([
        authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/plotly_figure.json`),
        authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/meta.json`)
      ]);
      if (!figRes.ok) throw new Error('No latest');
      const fig = await figRes.json();
      const meta = metaRes.ok ? await metaRes.json() : {};
      try { withSnapshotGuard(fig, { hash: computeFigureHash(fig), updatedAt: String((meta as any)?.updatedAt || (meta as any)?.createdAt || '') }, 'manual', { force: true }); } catch {}
      const etagKey = `lumiplot:plot:${plotId}:etag`;
      const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
      if (meta?.etag) localStorage.setItem(etagKey, String(meta.etag));
      if (meta?.updatedAt) localStorage.setItem(updatedKey, String(meta.updatedAt));
      setConflict(null);
      toast({ title: 'Reloaded latest', description: 'Editor updated to the latest snapshot.' });
    } catch {
      toast({ title: 'Reload failed', description: 'Could not load latest snapshot.', variant: 'destructive' });
    }
  };

  const overwriteAnyway = async () => {
    try {
      const plotId = (currentPlot as any)?.id;
      const plotlyJson = (currentPlot as any)?.plotlyJson;
      if (!plotId || !plotlyJson) return;
      // Infer projectId for snapshot meta so gallery aggregator can include it
      let inferredProjectId: string | undefined;
      try {
        const m = window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/);
        inferredProjectId = m?.[1];
      } catch {}
      const ifMatch = conflict?.etag || conflict?.updatedAt || '';
      const resp = await authorizedFetch(`/api/ai/v2/plots/${plotId}/save-snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(ifMatch ? { 'If-Match': ifMatch } : {}) },
        body: JSON.stringify({
          currentPlotlyJson: plotlyJson,
          projectId: inferredProjectId,
          scriptPy: (currentPlot as any)?.pythonCode || undefined,
          editedScriptPy: (currentPlot as any)?.pythonCode || undefined,
          statsJson: (currentPlot as any)?.statsJson || undefined,
          titleHint: (currentPlot as any)?.title || undefined,
        })
      });
      if (resp.status === 409) {
        const body = await resp.json().catch(() => ({}));
        setConflict({ etag: body?.etag, updatedAt: body?.updatedAt });
        toast({ 
          title: 'Concurrent save detected', 
          description: 'Another save operation is in progress. Please wait a moment and try again.', 
          variant: 'destructive',
          duration: 5000
        });
        return;
      }
      const json = await resp.json();
      if ((json as any)?.success) {
        const etagKey = `lumiplot:plot:${plotId}:etag`;
        const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
        if ((json as any).etag) localStorage.setItem(etagKey, String((json as any).etag));
        if ((json as any).updatedAt) localStorage.setItem(updatedKey, String((json as any).updatedAt));
        setConflict(null);
        toast({ title: 'Saved', description: 'Snapshot overwritten.' });
      } else {
        toast({ title: 'Save failed', description: 'Could not overwrite snapshot.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Save failed', description: 'Could not overwrite snapshot.', variant: 'destructive' });
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + S to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSaving, currentPlot]);

  // Dirty tracking is handled by the editor/store; no local tracking here

  // beforeunload handled at EditorPage level

  // const mockPythonCode = '';

  // Extract plotly data from currentPlot - use plotlyJson
  const { plotlyData, plotlyLayout, plotlyFrames } = useMemo(() => {
    let data: any, layout: any, frames: any;
    
    if (currentPlot) {
      // Use plotlyJson as the source
      if (currentPlot.plotlyJson) {
        const pj: any = currentPlot.plotlyJson as any;
        data = pj.data || [];
        layout = pj.layout || {};
        frames = pj.frames || undefined;
        console.log('🎨 PlotCanvas using plotlyJson:', {
          hasData: !!data && data.length > 0,
          dataLength: data?.length,
          hasLayout: !!layout,
          hasAnnotations: !!layout?.annotations,
          annotationCount: layout?.annotations?.length || 0,
          annotations: layout?.annotations,
          editrevision: layout?.editrevision,
          firstTraceMarkerColor: data?.[0]?.marker?.color,
          traceTypes: data?.map((t: any) => ({ type: t?.type, mode: t?.mode, hasValue: typeof t?.value === 'number' }))
        });
        // Prevent clipping of text labels at axis boundaries
        if (Array.isArray(data)) {
          data = data.map((t: any) => {
            const nt: any = { ...t };
            nt.cliponaxis = false;
            return nt;
          });
        }

        // Apply Lumi theme defaults (categorical colorway and continuous colorscales) without overwriting explicit colors
        try {
          // Work on a shallow copy of layout to avoid mutating editor state implicitly
          layout = { ...(layout || {}) } as any;
          const theme = (layout?.meta as any)?.lumi?.theme || {};
          const chosenRow: string[] | undefined = (
            Array.isArray((theme as any).categorical) && (theme as any).categorical.length
              ? (theme as any).categorical
              : (Array.isArray(theme.swatches) && theme.swatches.length
                ? theme.swatches
                : undefined)
          );
          const continuous: string = (theme?.sequential?.scale) || (typeof (theme as any).continuous === 'string' ? (theme as any).continuous : 'Viridis');
          const shareColoraxis: boolean = typeof (theme as any)?.coloraxis === 'string' && String((theme as any).coloraxis).length > 0;
          const coloraxisId: string = (shareColoraxis ? (theme as any).coloraxis : 'coloraxis') as string;
          const isDiverging = (theme?.mode === 'diverging');

          // Set colorway defaults (categorical) if not already present; fallback for high-cardinality
          const themeMappings: any = (theme as any)?.mappings || {};
          // High-cardinality fallback: when domain exceeds chosenRow, generate hue+lightness ring
          const effectiveSwatches = (function(){
            const domainLen = Array.isArray(themeMappings?.domain) ? themeMappings.domain.length : 0;
            if (chosenRow && chosenRow.length > 0) {
              if (domainLen > chosenRow.length) {
                const generated = generateHueLightnessPalette(domainLen);
                return generated && generated.length ? generated : chosenRow;
              }
              return chosenRow;
            }
            return chosenRow;
          })();
          // Apply categorical reversed if requested
          const catReversed = !!(theme as any)?.categoricalReversed;
          const swatchesToApply = effectiveSwatches ? (catReversed ? effectiveSwatches.slice().reverse() : effectiveSwatches) : effectiveSwatches;
          if (swatchesToApply && swatchesToApply.length > 0) {
            // Always overwrite colorways when a palette is in effect
            layout.colorway = swatchesToApply;
            (layout as any).sunburstcolorway = swatchesToApply;
            (layout as any).treemapcolorway = swatchesToApply;
            (layout as any).iciclecolorway = swatchesToApply;
            (layout as any).funnelareacolorway = swatchesToApply;
            (layout as any).extendsunburstcolors = true;
          }

          // (removed precompute array stops; using named scale + reversescale to flip progression in place)

          // Precompute scale spec with broad scope so it can be reused below
          const scaleName: any = continuous;
          let scaleSpec: any[] | string = String(scaleName);

          // Apply continuous colorscale to coloraxis; respect reversed and diverging settings
          try {
            const reversed = !!(theme?.sequential?.reversed || theme?.diverging?.reversed);
            // Normalize to an explicit colorscale array for names that Plotly may not recognize
            const namedStops: Record<string, string[]> = {
              Viridis: ['#440154','#3b528b','#21918c','#5ec962','#fde725'],
              Plasma: ['#0d0887','#6a00a8','#b12a90','#e16462','#fca636'],
              Cividis: ['#00204c','#414766','#7f7f6e','#b9b97a','#ffffe0'],
              Turbo: ['#30123b','#3a7cbf','#29af7f','#95d840','#fde725'],
              Inferno: ['#000004','#420a68','#932567','#dd513a','#fca50a'],
              Magma: ['#000004','#3b0f70','#8c2981','#de4968','#fdea45'],
              Blues: ['#f7fbff','#c6dbef','#6baed6','#3182bd','#08519c'],
              Greens: ['#f7fcf5','#c7e9c0','#74c476','#31a354','#006d2c'],
              Greys: ['#ffffff','#d9d9d9','#969696','#636363','#252525'],
              OrRd: ['#fff7ec','#fdd49e','#fdbb84','#ef6548','#b30000'],
              PuBuGn: ['#fff7fb','#ccebc5','#a8ddb5','#43a2ca','#016c59']
            };
            const toScaleArray = (stops: string[]): any[] => {
              const n = Math.max(2, stops.length);
              return stops.map((c, i) => [i / (n - 1), c]);
            };
            scaleSpec = namedStops[String(scaleName)] ? toScaleArray(namedStops[String(scaleName)]) : String(scaleName);
            if (shareColoraxis) {
              const ca: any = (layout as any)[coloraxisId] || {};
              const nextCa: any = { ...ca, colorscale: scaleSpec, reversescale: reversed };

              // Range/min/max/midpoint
              if (isDiverging) {
                const mid = (theme?.diverging?.midpoint ?? 0) as number;
                const customMin = theme?.diverging?.min;
                const customMax = theme?.diverging?.max;
                if (customMin != null && customMax != null) {
                  nextCa.cauto = false;
                  nextCa.cmin = customMin;
                  nextCa.cmax = customMax;
                }
                nextCa.cmid = mid;
              } else {
                const customMin = theme?.sequential?.min;
                const customMax = theme?.sequential?.max;
                if (customMin != null && customMax != null) {
                  nextCa.cauto = false;
                  nextCa.cmin = customMin;
                  nextCa.cmax = customMax;
                }
              }
              // NaN color
              if ((layout as any)[coloraxisId]?.nancolor) {
                nextCa.nancolor = (layout as any)[coloraxisId].nancolor;
              }

              // Fallback: infer cmin/cmax from data when not provided
              try {
                let inferredMin = Number.POSITIVE_INFINITY;
                let inferredMax = Number.NEGATIVE_INFINITY;
                const isNumericArray = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
                const hasZ = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));
                const zScaleTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'densitymapbox', 'choropleth', 'choroplethmapbox', 'image']);
                const markerScaleTypes = new Set(['scatter', 'scattergl', 'scattergeo', 'scattermapbox', 'splom', 'bar']);
                if (Array.isArray(data)) {
                  for (const tr of data as any[]) {
                    const t = String((tr?.type || 'scatter')).toLowerCase();
                    if (zScaleTypes.has(t) && hasZ(tr)) {
                      const z: any = tr.z;
                      if (Array.isArray(z?.[0])) {
                        for (const row of z as any[]) {
                          for (const v of row as any[]) {
                            if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
                          }
                        }
                      } else if (Array.isArray(z)) {
                        for (const v of z as any[]) { if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; } }
                      }
                    }
                    if (markerScaleTypes.has(t) && tr.marker && isNumericArray(tr.marker.color)) {
                      for (const v of tr.marker.color as any[]) { if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; } }
                    }
                    const hierTypes = new Set(['treemap', 'sunburst', 'icicle']);
                    if (hierTypes.has(t) && tr.marker && isNumericArray(tr.marker.colors)) {
                      for (const v of tr.marker.colors as any[]) { if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; } }
                    }
                    if ((t === 'parcoords' || t === 'parcats') && tr.line && isNumericArray(tr.line.color)) {
                      for (const v of tr.line.color as any[]) { if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; } }
                    }
                  }
                }
                if (isFinite(inferredMin) && isFinite(inferredMax)) {
                  if (nextCa.cmin == null) nextCa.cmin = inferredMin;
                  if (nextCa.cmax == null) nextCa.cmax = inferredMax;
                  if (nextCa.cauto === undefined) nextCa.cauto = false;
                }
              } catch {}

              (layout as any)[coloraxisId] = nextCa;
            }
          } catch {}

          // Helper: detect numeric arrays
          const isNumericArray = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
          const hasZ = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));
          const mappings: any = (theme as any)?.mappings || {};

          if (Array.isArray(data)) {
            data = data.map((tr: any, traceIndex: number) => {
              const next: any = { ...tr };
              // Prevent clipping of text labels at axis boundaries
              next.cliponaxis = false;
              const t = String((next.type || 'scatter') as string).toLowerCase();

              // A) Apply mapping table first when available; detect new categories
              if (mappings && mappings.colors && Object.keys(mappings.colors).length > 0) {
                const miss = mappings.missing || '#9e9e9e';
                // Per-series mapping via trace.name
                // For Indicator traces without names, skip mapping (will use default colorway colors)
                if (typeof next.name === 'string' && !next.marker?.color && !next.line?.color) {
                  const mapped = mappings.colors[next.name] || miss;
                  if (t === 'scatter' || t === 'scattergl' || t === 'line') {
                    next.line = next.line || {};
                    // Respect decouple hint
                    if (!(next as any)._lumiTheme?.decoupledLine) {
                      next.marker = next.marker || {};
                      if (next.marker.color === undefined) next.marker.color = mapped;
                    }
                    if (next.line.color === undefined) next.line.color = mapped;
                  } else if (t === 'bar' || t === 'histogram' || t === 'funnel') {
                    next.marker = next.marker || {};
                    if (next.marker.color === undefined) next.marker.color = mapped;
                  } else if (t === 'indicator') {
                    // Apply mapped color to gauge.bar.color for Indicator traces
                    if (!next.gauge) next.gauge = {};
                    if (!next.gauge.bar) next.gauge.bar = {};
                    // Only apply mapped color if color is undefined and not locked
                    // This respects script-generated colors by default
                    if (!((next as any)._lumiTheme?.lockedBarColor === true) && next.gauge.bar.color === undefined) {
                      next.gauge.bar.color = mapped;
                    }
                  }
                }
                // Indicator traces without names: will use default colorway in section 9 based on traceIndex
                // Per-slice mapping for pie/funnelarea by labels
                if ((t === 'pie' || t === 'funnelarea') && Array.isArray(next.labels)) {
                  next.marker = next.marker || {};
                  if (!next.marker.colors) {
                    next.marker.colors = (next.labels as any[]).map((v: any) => mappings.colors[String(v)] || miss);
                  }
                }
                // New category detection for categorical traces by labels or names
                try {
                  const currentDomain = Array.isArray(mappings.domain) ? mappings.domain.map(String) : [];
                  const candidates: string[] = [];
                  if (typeof next.name === 'string') candidates.push(String(next.name));
                  if (Array.isArray(next.labels)) candidates.push(...(next.labels as any[]).map(String));
                  const unseen = candidates.filter(v => !currentDomain.includes(v));
                  if (unseen.length > 0) {
                    // Fire a custom event for UI to surface a chip
                    try { window.dispatchEvent(new CustomEvent('lumi:newCategoriesDetected', { detail: { values: unseen } })); } catch {}
                  }
                } catch {}
              }

              // 1) Traces with top-level colorscale (z-based)
              const zScaleTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'densitymapbox', 'choropleth', 'choroplethmapbox', 'image']);
              // Handle z data for both known z-scale types AND any trace with z data
              if (hasZ(next)) {
                // Prefer shared coloraxis binding when theme requests it; otherwise always apply theme continuous scale
                if (shareColoraxis) {
                  next.coloraxis = coloraxisId;
                  delete (next as any).colorscale;
                  delete (next as any).autocolorscale;
                } else if (!((next as any)._lumiTheme?.lockedScale === true)) {
                  delete (next as any).colorscale;
                  delete (next as any).autocolorscale;
                  next.colorscale = scaleSpec;
                  (next as any).reversescale = !!(theme?.sequential?.reversed || theme?.diverging?.reversed);
                }
              }
              // For known z-scale types, also ensure no dimming overlay on image-like layers
              if (zScaleTypes.has(t)) {
                if (typeof next.opacity === 'number') {
                  const n = { ...next } as any; delete n.opacity; return n;
                }
              }

              // 2) Scatter-like and bar with marker color arrays
              const markerScaleTypes = new Set(['scatter', 'scattergl', 'scattergeo', 'scattermapbox', 'splom', 'bar']);
              if (markerScaleTypes.has(t)) {
                if (next.marker && isNumericArray(next.marker.color)) {
                  if (shareColoraxis) {
                    next.marker = { ...next.marker, coloraxis: coloraxisId } as any;
                    delete (next.marker as any).colorscale;
                    delete (next.marker as any).autocolorscale;
                  } else if (!((next as any)._lumiTheme?.lockedScale === true)) {
                    delete (next.marker as any).colorscale;
                    delete (next.marker as any).autocolorscale;
                    next.marker = { ...next.marker, colorscale: scaleSpec, reversescale: !!(theme?.sequential?.reversed || theme?.diverging?.reversed) } as any;
                  }
                  // Clamp numeric arrays if requested
                  try {
                    const min = (theme?.sequential?.min ?? theme?.diverging?.min) as any;
                    const max = (theme?.sequential?.max ?? theme?.diverging?.max) as any;
                    const doClamp = !!(theme?.notes?.clamp) && (min != null || max != null);
                    if (doClamp && Array.isArray(next.marker.color)) {
                      const lo = typeof min === 'number' ? min : -Infinity;
                      const hi = typeof max === 'number' ? max : Infinity;
                      next.marker.color = next.marker.color.map((v: any) => (typeof v === 'number' ? (v < lo ? lo : v > hi ? hi : v) : v));
                    }
                  } catch {}
                }
              }

              // 3) Parallel coordinates with line color arrays
              if (t === 'parcoords') {
                if (next.line && typeof next.line === 'object' && isNumericArray(next.line.color)) {
                  delete (next.line as any).colorscale;
                  next.line = { ...next.line, colorscale: scaleSpec, reversescale: !!(theme?.sequential?.reversed || theme?.diverging?.reversed) } as any;
                }
              }

              // 3b) Parallel categories (parcats)
              if (t === 'parcats') {
                if (next.line && typeof next.line === 'object' && isNumericArray(next.line.color)) {
                  delete (next.line as any).colorscale;
                  next.line = { ...next.line, colorscale: scaleSpec, reversescale: !!(theme?.sequential?.reversed || theme?.diverging?.reversed) } as any;
                }
              }

              // 4) Treemap/Sunburst/Icicle can use marker.colorscale when colored by values
              const hierTypes = new Set(['treemap', 'sunburst', 'icicle']);
              if (hierTypes.has(t)) {
                if (next.marker && isNumericArray(next.marker.colors)) {
                  if (shareColoraxis) {
                    next.marker = { ...next.marker, coloraxis: coloraxisId } as any;
                    delete (next.marker as any).colorscale;
                    delete (next.marker as any).autocolorscale;
                  } else if (!((next as any)._lumiTheme?.lockedScale === true)) {
                    delete (next.marker as any).colorscale;
                    delete (next.marker as any).autocolorscale;
                    next.marker = { ...next.marker, colorscale: scaleSpec, reversescale: !!(theme?.sequential?.reversed || theme?.diverging?.reversed) } as any;
                  }
                }
              }

              // 5) Sankey: apply categorical colors to nodes and links
              if (t === 'sankey' && effectiveSwatches && effectiveSwatches.length > 0) {
                const node = next.node || {};
                const labels = node.labels || node.label; // Plotly accepts `label`
                if (Array.isArray(labels)) {
                  // Always apply theme colors to nodes (unless locked)
                  // This normalizes all nodes to use theme colors, overriding Python-set colors
                  if (!((next as any)._lumiTheme?.lockedNodeColors === true)) {
                    const nodeColors = labels.map((_: any, i: number) => {
                      // Check if there's a mapping for this label
                      const labelStr = String(_);
                      if (mappings?.colors && mappings.colors[labelStr]) {
                        return mappings.colors[labelStr];
                      }
                      // Apply theme color cyclically
                      return effectiveSwatches[i % effectiveSwatches.length];
                    });
                    next.node = { ...node, color: nodeColors };
                  }
                  
                  // Always apply theme colors to links based on source node colors (unless locked)
                  const link = next.link || {};
                  if (Array.isArray(link.source) && Array.isArray(link.target) && 
                      !((next as any)._lumiTheme?.lockedLinkColors === true)) {
                    // Get current node colors (either from theme or locked)
                    const sourceColors = (next.node?.color || []).slice();
                    const linkColors = link.source.map((srcIdx: number) => {
                      // Use source node color, with opacity for links
                      const srcColor = sourceColors[srcIdx] || effectiveSwatches[srcIdx % effectiveSwatches.length];
                      // Convert hex to rgba with opacity if needed
                      if (srcColor.startsWith('#')) {
                        // Add opacity suffix (80 = 50% opacity in hex)
                        return srcColor + '80';
                      } else if (srcColor.startsWith('rgba')) {
                        // Already rgba, just return
                        return srcColor;
                      } else {
                        // Try to add opacity
                        return srcColor + '80';
                      }
                    });
                    next.link = { ...link, color: linkColors };
                  }
                }
              }

              // 6) Splom diagonals/axes sometimes use marker; already covered by markerScaleTypes

              // 7) Financial traces: set default increasing/decreasing if absent
              if ((t === 'ohlc' || t === 'candlestick') && effectiveSwatches && effectiveSwatches.length >= 2) {
                const inc = next.increasing || {};
                const incLine = inc.line || {};
                const dec = next.decreasing || {};
                const decLine = dec.line || {};
                if (incLine.color === undefined) incLine.color = effectiveSwatches[0];
                if (decLine.color === undefined) decLine.color = effectiveSwatches[1 % effectiveSwatches.length];
                next.increasing = { ...inc, line: incLine };
                next.decreasing = { ...dec, line: decLine };
              }

              // 8) Waterfall defaults
              if (t === 'waterfall' && effectiveSwatches && effectiveSwatches.length >= 3) {
                const inc = next.increasing || {};
                const incMarker = inc.marker || {};
                if (incMarker.color === undefined) incMarker.color = effectiveSwatches[0];
                next.increasing = { ...inc, marker: incMarker };

                const dec = next.decreasing || {};
                const decMarker = dec.marker || {};
                if (decMarker.color === undefined) decMarker.color = effectiveSwatches[1 % effectiveSwatches.length];
                next.decreasing = { ...dec, marker: decMarker };

                const totals = next.totals || {};
                const totalsMarker = totals.marker || {};
                if (totalsMarker.color === undefined) totalsMarker.color = effectiveSwatches[2 % effectiveSwatches.length];
                next.totals = { ...totals, marker: totalsMarker };
                // Override with theme roles when present
                try {
                  const roles:any = (theme as any).roles || {};
                  if (roles.increasing) (next.increasing = { ...(next.increasing || {}), marker: { ...(next.increasing?.marker || {}), color: roles.increasing } });
                  if (roles.decreasing) (next.decreasing = { ...(next.decreasing || {}), marker: { ...(next.decreasing?.marker || {}), color: roles.decreasing } });
                  if (roles.totals) (next.totals = { ...(next.totals || {}), marker: { ...(next.totals?.marker || {}), color: roles.totals } });
                } catch {}
              }

              // 9) Apply categorical color per trace when no explicit color is set
              if (effectiveSwatches && effectiveSwatches.length > 0) {
                const defaultColor = effectiveSwatches[traceIndex % effectiveSwatches.length];
                if (t === 'bar' || t === 'histogram' || t === 'scatter' || t === 'scattergl' || t === 'splom' || t === 'scattergeo' || t === 'scattermapbox' || t === 'funnel') {
                  if (!next.marker) next.marker = {};
                  if (next.marker.color === undefined && next.marker.colorscale === undefined && next.marker.coloraxis === undefined && !isNumericArray(next.marker.color)) {
                    next.marker.color = defaultColor;
                  }
                  // For scatter lines only
                  if ((t === 'scatter' || t === 'scattergl') && next.mode && next.mode.includes('lines')) {
                    if (!next.line) next.line = {};
                    if (next.line.color === undefined) next.line.color = defaultColor;
                  }
                } else if (t === 'indicator') {
                  // Indicator traces: apply categorical colors to gauge.bar.color
                  // Only apply if color is not already set (consistent with other trace types)
                  if (!next.gauge) next.gauge = {};
                  if (!next.gauge.bar) next.gauge.bar = {};
                  // Only apply theme color if color is undefined and not locked
                  // This respects script-generated colors by default
                  if (!((next as any)._lumiTheme?.lockedBarColor === true) && next.gauge.bar.color === undefined) {
                    next.gauge.bar.color = defaultColor;
                  }
                  // Also apply to delta.value.color if delta exists
                  if (next.delta && next.delta.value && !((next as any)._lumiTheme?.lockedDeltaColor === true)) {
                    next.delta.value.color = defaultColor;
                  }
                  
                  // Apply theme colors to steps (background bands) if not locked
                  // Steps typically have 3 bands: poor, satisfactory, good
                  // We'll use a subset of the colorway for steps
                  if (Array.isArray(next.gauge.steps) && next.gauge.steps.length > 0) {
                    const lockedSteps = (next as any)._lumiTheme?.lockedStepColors || {};
                    // Use colorway colors for steps: typically red/yellow/green pattern
                    // But allow theme to override if not manually locked
                    const stepColorway = effectiveSwatches.length >= 3 
                      ? [effectiveSwatches[1], effectiveSwatches[3], effectiveSwatches[2]] // red, yellow, green from colorway
                      : effectiveSwatches;
                    next.gauge.steps = next.gauge.steps.map((step: any, stepIdx: number) => {
                      if (!lockedSteps[stepIdx]) {
                        // Apply theme color to step if not locked
                        return { ...step, color: stepColorway[stepIdx % stepColorway.length] };
                      }
                      return step;
                    });
                  }
                  
                  // Apply theme color to threshold line if not locked
                  if (next.gauge.threshold && !((next as any)._lumiTheme?.lockedThresholdColor === true)) {
                    if (!next.gauge.threshold.line) next.gauge.threshold.line = {};
                    // Use a darker color from colorway for threshold line (typically purple/dark blue)
                    const thresholdColor = effectiveSwatches.length >= 7 
                      ? effectiveSwatches[7] // purple from colorway
                      : effectiveSwatches[effectiveSwatches.length - 1];
                    if (!next.gauge.threshold.line.color) {
                      next.gauge.threshold.line.color = thresholdColor;
                    }
                  }
                } else if (t === 'box' || t === 'violin') {
                  if (!next.line) next.line = {};
                  if (next.line.color === undefined) next.line.color = defaultColor;
                  if (next.fillcolor === undefined) next.fillcolor = defaultColor + '40';
                } else if (t === 'pie' || t === 'funnelarea') {
                  // Apply categorical across slices when not explicitly set
                  const labels = next.labels || next.label;
                  if (Array.isArray(labels)) {
                    if (!next.marker) next.marker = {};
                    if (next.marker.colors === undefined) {
                      next.marker.colors = labels.map((_: any, i: number) => effectiveSwatches[i % effectiveSwatches.length]);
                    }
                  }
                }
              }

              return next;
            });
          }
        } catch {}
      }
      // Check if we have _apiData structure
      else if ((currentPlot as any)?._apiData?.generation?.plotlyJson) {
        const apiPlot: any = (currentPlot as any)._apiData;
        data = apiPlot.generation.plotlyJson.data;
        // Apply cliponaxis: false to all traces in legacy path
        if (Array.isArray(data)) {
          data = data.map((t: any) => ({ ...t, cliponaxis: false }));
        }
        layout = apiPlot.generation.plotlyJson.layout;
        frames = apiPlot.generation.plotlyJson.frames || undefined;
        console.log('⚠️ PlotCanvas using _apiData (legacy)');
      }
      // Fallback to default data
      else {
        // Keep clean (no default traces) on fresh or new pages
        data = [];
        layout = {
          plot_bgcolor: 'white',
          paper_bgcolor: 'white',
          font: plotlyTypographyDefaults.font,
          margin: { t: 200, r: 80, b: 60, l: 60 },
          width: undefined,
          height: undefined,
          annotations: [],
          editrevision: 0
        } as any;
        frames = undefined;
      }
    } else {
      // No plot selected - keep canvas empty (no default traces)
      data = [];
      layout = {
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        font: plotlyTypographyDefaults.font,
        margin: { t: 200, r: 80, b: 60, l: 60 },
        editrevision: 0
      } as any;
      frames = undefined;
    }
    
    // Force empty on explicitly fresh loads
    try { if ((window as any).__lumiplotFresh) { data = []; } } catch {}

    return { plotlyData: data, plotlyLayout: layout, plotlyFrames: frames } as any;
  }, [currentPlot?.plotlyJson]); // Depend on plotlyJson object identity

  // Auto preview scaling: fit small journal figures to available space (editor-only)
  useEffect(() => {
    const compute = () => {
      try {
        const el = containerRef.current as HTMLDivElement | null;
        if (!el) return;
        const cw = Math.max(100, el.clientWidth);
        const ch = Math.max(100, el.clientHeight);
        const w = typeof (plotlyLayout as any)?.width === 'number' ? (plotlyLayout as any).width : cw;
        const h = typeof (plotlyLayout as any)?.height === 'number' ? (plotlyLayout as any).height : ch;
        const scaleX = cw / Math.max(1, w);
        const scaleY = ch / Math.max(1, h);
        const s = Math.min(3, Math.max(1, Math.min(scaleX, scaleY) * 0.9));
        setAutoScale(s);
      } catch {}
    };
    const pref = (plotlyLayout as any)?._previewScale;
    if (pref === undefined || pref === null || pref === 'auto') {
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    } else {
      setAutoScale(1);
    }
  }, [plotlyLayout?.width, plotlyLayout?.height, (plotlyLayout as any)?._previewScale]);

  // Debug logging
  useEffect(() => {
    console.log('🎨 PlotCanvas state update:');
    console.log('  currentPlot:', currentPlot?.id);
    console.log('  has plotlyJson:', !!currentPlot?.plotlyJson);
    console.log('  plotlyData length:', (plotlyData as any)?.length);
    console.log('  annotations:', (plotlyLayout as any)?.annotations?.length || 0);
    console.log('  shapes:', (plotlyLayout as any)?.shapes?.length || 0);
    console.log('  editrevision:', (plotlyLayout as any)?.editrevision || 0);
    console.log('  firstTraceMarkerColor:', (plotlyData as any)?.[0]?.marker?.color);
  }, [currentPlot, plotlyData, plotlyLayout]);

  // Helper to check if there's plot data to enable adaptive UI
  const hasPlotData = useMemo(() => {
    if (!plotlyData || !Array.isArray(plotlyData)) return false;
    return plotlyData.length > 0 && plotlyData.some((trace: any) => {
      // Check if trace has any data (x, y, z, values, etc.)
      return (Array.isArray(trace?.x) && trace.x.length > 0) ||
             (Array.isArray(trace?.y) && trace.y.length > 0) ||
             (Array.isArray(trace?.z) && trace.z.length > 0) ||
             (Array.isArray(trace?.values) && trace.values.length > 0) ||
             (typeof trace?.value === 'number') ||
             (Array.isArray(trace?.labels) && trace.labels.length > 0);
    });
  }, [plotlyData]);

  // Handle plotly click events
  const handlePlotlyClick = (data: any) => {
    // Only activate adaptive UI if there's plot data
    if (!hasPlotData) return;
    
    if (data?.points?.length > 0) {
      const point = data.points[0];
      // Use curveNumber for trace index, pointNumber/pointIndex for point index
      const traceIndex = point.curveNumber;
      const pointIndex = point.pointIndex !== undefined ? point.pointIndex : point.pointNumber;
      
      // Use new selection actions with 'canvas' source
      selectPoint(traceIndex, pointIndex, 'canvas');
    } else {
      // Clicked on empty area
      clearSelection('canvas');
    }
  };

  // Handle box/lasso selection events for multiple points
  const handlePlotlySelected = (data: any) => {
    // Only activate adaptive UI if there's plot data
    if (!hasPlotData) return;
    
    if (!data || !Array.isArray(data.points) || data.points.length === 0) {
      clearSelection('canvas');
      return;
    }
    // Pick the first point for the editor; remember the series
    const p = data.points[0];
    const traceIndex = p.curveNumber;
    const pointIndex = p.pointIndex !== undefined ? p.pointIndex : p.pointNumber;
    selectPoint(traceIndex, pointIndex, 'canvas');
  };

  const handlePlotlyDeselect = () => {
    // Only activate adaptive UI if there's plot data
    if (!hasPlotData) return;
    clearSelection('canvas');
  };

  // Handle clicking on empty plot area to clear selection
  // const handlePlotlyClickBackground = (data: any) => {
  //   if (!data?.points || data.points.length === 0) {
  //     setSelectedPoint(null);
  //   }
  // };

  // Handle Plotly relayout events (persist user edits back to state)
  const handlePlotlyRelayout = (data: any) => {
    console.log('🔄 Plotly relayout event:', data);
    if (!currentPlot?.plotlyJson || !data) return;

    // Ignore resize-only noise
    if ((data as any).autosize !== undefined || (data as any).width !== undefined || (data as any).height !== undefined) {
      return;
    }

    // Helper: set layout path from relayout key (supports bracket indices)
    const setLayoutPath = (json: any, keyPath: string, value: any) => {
      const parts = keyPath.replace(/\[(\d+)\]/g, '.$1').split('.');
      let target = json.layout || (json.layout = {});
      for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i];
        const nextIsIndex = /^\d+$/.test(parts[i + 1] || '');
        if (target[seg] === undefined) target[seg] = nextIsIndex ? [] : {};
        target = target[seg];
      }
      const last = parts[parts.length - 1];
      if (/^\d+$/.test(last) && Array.isArray(target)) {
        target[Number(last)] = value;
      } else {
        target[last] = value;
      }
    };

    const updatedJson: any = structuredClone(currentPlot.plotlyJson as any);
    const entries = Object.entries(data);
    const bracketShapeIndices = new Set<number>();

    for (const [key, value] of entries) {
      // Annotations
      const ann = key.match(/annotations\[(\d+)\]\.(.+)/);
      if (ann) {
        const [, indexStr, property] = ann as any;
        const index = parseInt(indexStr);
        if (!updatedJson.layout.annotations) updatedJson.layout.annotations = [];
        if (!updatedJson.layout.annotations[index]) updatedJson.layout.annotations[index] = {};
        const props = property.split('.');
        let target = updatedJson.layout.annotations[index];
        for (let i = 0; i < props.length - 1; i++) {
          if (!target[props[i]]) target[props[i]] = {};
          target = target[props[i]];
        }
        target[props[props.length - 1]] = value as any;
        continue;
      }

      // Shapes
      const shp = key.match(/shapes\[(\d+)\]\.(.+)/);
      if (shp) {
        const [, indexStr, property] = shp as any;
        const index = parseInt(indexStr);
        if (!updatedJson.layout.shapes) updatedJson.layout.shapes = [];
        if (!updatedJson.layout.shapes[index]) updatedJson.layout.shapes[index] = {};
        const props = property.split('.');
        let target = updatedJson.layout.shapes[index];
        for (let i = 0; i < props.length - 1; i++) {
          if (!target[props[i]]) target[props[i]] = {};
          target = target[props[i]];
        }
        target[props[props.length - 1]] = value as any;

        const shapeRef = updatedJson.layout.shapes[index];
        if (shapeRef?.type === 'path' && parseBracketPath(shapeRef.path)) {
          bracketShapeIndices.add(index);
        }
        continue;
      }

      // General relayout keys (e.g., legend.x, title.text, xaxis.title.text, xaxis.range[0])
      if (key !== 'autosize' && key !== 'width' && key !== 'height') {
        try { setLayoutPath(updatedJson, key, value); } catch (e) { console.warn('relayout path set failed', key, e); }
      }
    }

    bracketShapeIndices.forEach((shapeIdx) => {
      const shape = updatedJson.layout?.shapes?.[shapeIdx];
      if (shape) {
        syncBracketShapeAndLabel(updatedJson.layout, shape);
      }
    });

    const updatedPlot = {
      ...currentPlot,
      plotlyJson: updatedJson,
      lastEdit: { type: 'relayout', value: data, timestamp: Date.now() }
    } as any;
    setCurrentPlot(updatedPlot);

    console.debug('📍 Relayout synced:', {
      keys: Object.keys(data)
    });

    try { markUserEdited?.(); } catch {}
  };

  const handleExport = async (format: "png" | "svg" | "pdf" | "code") => {
    // Track CTA click for onboarding telemetry
    if (format !== 'code') {
      try {
        const { trackCTAClick } = await import('@/utils/telemetry');
        trackCTAClick('download', { format });
      } catch {}
    }
    if (!currentPlot) return;
    
    try {
      if (format === "code") {
        // Use new export API for clean, reproducible code
        const pid = (() => { try { const m = window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/); return m?.[1]; } catch { return undefined; } })();
        const response = await authorizedFetch(`/api/ai/v2/export-code${pid ? `?projectId=${encodeURIComponent(pid)}` : ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plotId: (currentPlot as any).id,
            format: 'python',
            currentPlotlyJson: (currentPlot as any).plotlyJson
          })
        });
        
        const result = await response.json();
        
        if ((result as any).success) {
          navigator.clipboard.writeText((result as any).code);
          toast({
            title: "Code copied to clipboard",
            description: "Clean, reproducible Python code has been copied to your clipboard.",
          });
          setCopiedCode(true);
          if (copiedCodeTimeoutRef.current) window.clearTimeout(copiedCodeTimeoutRef.current);
          // @ts-ignore setTimeout returns number in browsers
          copiedCodeTimeoutRef.current = window.setTimeout(() => setCopiedCode(false), 1500);
          
          // Also trigger download
          const blob = new Blob([(result as any).code], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = (result as any).filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
        } else {
          throw new Error((result as any).error);
        }
      } else {
        // For image exports, use Plotly's built-in functionality
        const plotElement = document.querySelector('.js-plotly-plot') as any;
        if (plotElement && plotElement.layout) {
          const dpi = plotElement?.layout?._export?.dpi || 300;
          const scaleFromDpi = Math.max(1, Math.round(dpi / 96));

          // Use layout sizes if provided, otherwise fallback to rendered size
          let outWidth = (plotElement.layout as any).width || (plotElement.clientWidth as number) || 800;
          let outHeight = (plotElement.layout as any).height || (plotElement.clientHeight as number) || 600;

          // Enforce journal maxima when applicable
          const pubMeta:any = (plotElement.layout as any)._publication;
          if (pubMeta?.activeTemplateId) {
            const t = journalTemplatePresets.find(x => x.id === pubMeta.activeTemplateId);
            const sp:any = t?.sizePolicy || {};
            if (sp.maxHeight_mm) {
              const maxHPx = mmToPx(sp.maxHeight_mm);
              if (outHeight > maxHPx) outHeight = maxHPx;
            }
            if (sp.width_mm_range) {
              const minPx = mmToPx(sp.width_mm_range[0]);
              const maxPx = mmToPx(sp.width_mm_range[1]);
              outWidth = Math.min(Math.max(outWidth, minPx), maxPx);
            }
            if (sp.maxSize_in) {
              const maxWPx = Math.round(sp.maxSize_in[0] * 96);
              const maxHPx = Math.round(sp.maxSize_in[1] * 96);
              outWidth = Math.min(outWidth, maxWPx);
              outHeight = Math.min(outHeight, maxHPx);
            }
          }

          const config = {
            format: format,
            width: outWidth,
            height: outHeight,
            scale: scaleFromDpi
          } as any;

          // Ensure tooltips are off in exports
          try {
            if (plotElement.layout.hovermode !== false) {
              plotElement.layout.hovermode = false;
            }
            if (Array.isArray(plotElement.data)) {
              for (const t of plotElement.data) {
                t.hoverinfo = 'skip';
                if (t.hovertemplate !== undefined) delete t.hovertemplate;
              }
            }
          } catch {}
          
          // Use Plotly.downloadImage if available
          if (window.Plotly && window.Plotly.downloadImage) {
            await window.Plotly.downloadImage(plotElement, config);
            toast({
              title: `Exported as ${format.toUpperCase()}`,
              description: "Your plot has been downloaded.",
            });
            try { track('chart:downloaded', { chart_id: String((currentPlot as any)?.id || 'unknown'), format: String(format) }); } catch {}
          } else {
            toast({
              title: `Exporting as ${format.toUpperCase()}`,
              description: "Your plot will be downloaded shortly.",
            });
          }
        }
      }
      setExportModalOpen(false);
    } catch (error) {
      toast({
        title: "Export failed",
        description: "There was an error exporting your plot.",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    if (!currentPlot) return;
    
    // Check if plot is saved (has latestVersionId) - drafts cannot be shared
    const isDraft = !(currentPlot as any)?.latestVersionId;
    if (isDraft) {
      toast({
        title: "Save required",
        description: "Please save your plot before sharing. Share links are only available for saved plots.",
        variant: "destructive",
      });
      return;
    }
    
    const plotlyJson = (currentPlot as any)?.plotlyJson;
    if (!plotlyJson) {
      toast({
        title: "Share failed",
        description: "Plot data is not available. Please wait for the plot to finish loading.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const response = await authorizedFetch('/api/ai/v2/share-plot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plotId: (currentPlot as any).id,
          currentPlotlyJson: plotlyJson,
          expiryDays: 7
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `Failed to create share link (${response.status})`);
      }
      
      const result = await response.json();
      
      if ((result as any).success) {
        setShareToken((result as any).shareToken);
        setShareModalOpen(true);
        toast({
          title: "Share link created",
          description: "Your plot is now available for sharing.",
        });
      } else {
        throw new Error((result as any).error || "Failed to create share link");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "There was an error creating the share link.";
      toast({
        title: "Share failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleExportPack = async () => {
    try {
      const plotId = (currentPlot as any)?.id;
      if (!plotId) throw new Error('Missing plot');
      const url = `/api/ai/v2/plots/${plotId}/export-pack.zip`;
      // Stream as a download
      const resp = await authorizedFetch(url);
      if (!resp.ok) throw new Error('Export pack failed');
      const cd = resp.headers?.get?.('Content-Disposition') || resp.headers?.get?.('content-disposition');
      let filename = '';
      try {
        if (cd) {
          const m = cd.match(/filename="?([^";]+)"?/i);
          if (m && m[1]) filename = m[1];
        }
      } catch {}
      const blob = await resp.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = filename || `lumiplot_export_${plotId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
      try { track('chart:downloaded', { chart_id: String(plotId), format: 'zip' }); } catch {}
      toast({ title: 'Export started', description: 'Export pack downloaded.' });
    } catch (e) {
      toast({ title: 'Export failed', description: 'Could not download export pack.', variant: 'destructive' });
    }
  };

  // duplicate flow removed; replaced by start-new-plot

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/share/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Share link copied",
      description: "The share link has been copied to your clipboard.",
    });
    setCopiedShare(true);
    if (copiedShareTimeoutRef.current) window.clearTimeout(copiedShareTimeoutRef.current);
    // @ts-ignore setTimeout returns number in browsers
    copiedShareTimeoutRef.current = window.setTimeout(() => setCopiedShare(false), 1500);
    // Track CTA click for onboarding telemetry
    try {
      import('@/utils/telemetry').then(({ trackCTAClick }) => {
        trackCTAClick('share');
      });
    } catch {}
  };

  // Handle legend clicks
  // Plotly legend events have curveNumber directly on the event object
  const handleLegendClick = (evt: any) => {
    // Only activate adaptive UI if there's plot data
    if (!hasPlotData) return;
    
    if (evt?.curveNumber !== undefined && evt.curveNumber !== null) {
      selectLegendItem(evt.curveNumber, 'canvas');
    }
  };

  // Attach axis title and plot title click handlers via DOM listeners
  // Guard: prevent re-attaching if already attached recently (debounce)
  const attachTitleHandlers = (gd: any) => {
    if (!gd) return () => {};
    
    // Debounce: prevent re-attaching if already attached recently
    if ((gd as any)._lumiTitleHandlersAttached) {
      const lastAttach = (gd as any)._lumiTitleHandlersAttached;
      if (Date.now() - lastAttach < 1000) {
        return () => {}; // Skip if attached within last second
      }
    }
    (gd as any)._lumiTitleHandlersAttached = Date.now();
    
    const cleanup = () => {
      try {
        // Try multiple selector patterns for Plotly axis titles
        const selectors = [
          '.xtitle', '.g-xtitle', '.xaxistitle', '[class*="xtitle"]',
          '.ytitle', '.g-ytitle', '.yaxistitle', '[class*="ytitle"]',
          '.x2title', '.g-x2title', '.x2axistitle', '[class*="x2title"]',
          '.y2title', '.g-y2title', '.y2axistitle', '[class*="y2title"]',
          '.gtitle', '.plottitle', '[class*="gtitle"]'
        ];
        
        const elements: Element[] = [];
        selectors.forEach(sel => {
          try {
            const found = gd.querySelectorAll(sel);
            elements.push(...Array.from(found));
          } catch {}
        });
        
        elements.forEach((el) => {
          if (el && (el as any)._lumiTitleClickHandler) {
            el.removeEventListener('click', (el as any)._lumiTitleClickHandler);
            delete (el as any)._lumiTitleClickHandler;
          }
        });
      } catch (e) {
        console.warn('Error cleaning up title handlers:', e);
      }
    };
    
    cleanup(); // Remove existing handlers first
    
    try {
      // Try multiple selector patterns - Plotly uses different class names in different versions
      const findElement = (selectors: string[]) => {
        for (const sel of selectors) {
          try {
            const el = gd.querySelector(sel);
            if (el) return el;
          } catch {}
        }
        return null;
      };
      
      const xAxisTitle = findElement(['.xtitle', '.g-xtitle', '.xaxistitle', 'g.xtitle', '[class*="xtitle"]']);
      const yAxisTitle = findElement(['.ytitle', '.g-ytitle', '.yaxistitle', 'g.ytitle', '[class*="ytitle"]']);
      const x2AxisTitle = findElement(['.x2title', '.g-x2title', '.x2axistitle', 'g.x2title', '[class*="x2title"]']);
      const y2AxisTitle = findElement(['.y2title', '.g-y2title', '.y2axistitle', 'g.y2title', '[class*="y2title"]']);
      const plotTitle = findElement(['.gtitle', '.plottitle', 'g.gtitle', '[class*="gtitle"]']);
      
      const handleXAxisClick = (e: Event) => {
        e.stopPropagation();
        // Only activate adaptive UI if there's plot data
        if (hasPlotData) {
          selectAxis('x', 'canvas');
        }
      };
      const handleYAxisClick = (e: Event) => {
        e.stopPropagation();
        // Only activate adaptive UI if there's plot data
        if (hasPlotData) {
          selectAxis('y', 'canvas');
        }
      };
      const handleX2AxisClick = (e: Event) => {
        e.stopPropagation();
        // Only activate adaptive UI if there's plot data
        if (hasPlotData) {
          selectAxis('x2', 'canvas');
        }
      };
      const handleY2AxisClick = (e: Event) => {
        e.stopPropagation();
        // Only activate adaptive UI if there's plot data
        if (hasPlotData) {
          selectAxis('y2', 'canvas');
        }
      };
      const handlePlotTitleClick = (e: Event) => {
        e.stopPropagation();
        // Only activate adaptive UI if there's plot data
        if (hasPlotData) {
          selectTitle('canvas');
        }
      };
      
      if (xAxisTitle) {
        xAxisTitle.addEventListener('click', handleXAxisClick, true); // Use capture phase
        (xAxisTitle as any)._lumiTitleClickHandler = handleXAxisClick;
        (xAxisTitle as any).style.cursor = 'pointer';
      }
      if (yAxisTitle) {
        yAxisTitle.addEventListener('click', handleYAxisClick, true);
        (yAxisTitle as any)._lumiTitleClickHandler = handleYAxisClick;
        (yAxisTitle as any).style.cursor = 'pointer';
      }
      if (x2AxisTitle) {
        x2AxisTitle.addEventListener('click', handleX2AxisClick, true);
        (x2AxisTitle as any)._lumiTitleClickHandler = handleX2AxisClick;
        (x2AxisTitle as any).style.cursor = 'pointer';
      }
      if (y2AxisTitle) {
        y2AxisTitle.addEventListener('click', handleY2AxisClick, true);
        (y2AxisTitle as any)._lumiTitleClickHandler = handleY2AxisClick;
        (y2AxisTitle as any).style.cursor = 'pointer';
      }
      if (plotTitle) {
        plotTitle.addEventListener('click', handlePlotTitleClick, true);
        (plotTitle as any)._lumiTitleClickHandler = handlePlotTitleClick;
        (plotTitle as any).style.cursor = 'pointer';
      }
      
      // Also try to find axis titles by searching for text elements near axis labels
      // Plotly sometimes wraps titles in nested SVG/g elements
      setTimeout(() => {
        try {
          const allTexts = gd.querySelectorAll('text');
          allTexts.forEach((textEl: any) => {
            const text = textEl.textContent?.trim();
            const parent = textEl.closest('g');
            if (!text || !parent) return;
            
            // Check if this text is near axis area (heuristic)
            const bbox = textEl.getBBox?.();
            if (!bbox) return;
            
            // X axis titles are typically at bottom, Y axis titles are typically at left
            const isNearBottom = bbox.y > (gd.clientHeight || 0) * 0.7;
            const isNearLeft = bbox.x < (gd.clientWidth || 0) * 0.2;
            
            if (isNearBottom && !xAxisTitle && parent.classList.contains('xtitle')) {
              parent.addEventListener('click', handleXAxisClick, true);
              (parent as any).style.cursor = 'pointer';
            }
            if (isNearLeft && !yAxisTitle && parent.classList.contains('ytitle')) {
              parent.addEventListener('click', handleYAxisClick, true);
              (parent as any).style.cursor = 'pointer';
            }
          });
        } catch (e) {
          console.warn('Fallback title search failed:', e);
        }
      }, 200);
      
      return cleanup;
    } catch (e) {
      console.warn('Failed to attach title handlers', e);
      return cleanup;
    }
  };

  // Ensure Plotly native events are wired even if React handler misses
  const attachPlotlyHandlers = (gd: any) => {
    if (!gd || typeof gd.on !== 'function') return;
    try {
      if ((gd as any)._lumihandlers) return; // already attached
      const onClick = (evt: any) => handlePlotlyClick(evt);
      const onSel = (evt: any) => handlePlotlySelected(evt);
      const onDesel = () => handlePlotlyDeselect();
      const onLegendClick = (evt: any) => handleLegendClick(evt);
      
      gd.on('plotly_click', onClick);
      gd.on('plotly_selected', onSel);
      gd.on('plotly_deselect', onDesel);
      gd.on('plotly_legendclick', onLegendClick);
      gd.on('plotly_legenddoubleclick', onLegendClick);
      
      // Attach title handlers (axis titles and plot title) after plot renders
      const onAfterPlot = () => {
        // Debounce: reset attachment guard on afterplot so handlers can reattach
        delete (gd as any)._lumiTitleHandlersAttached;
        // Use multiple timeouts to catch titles that render at different times
        setTimeout(() => attachTitleHandlers(gd), 100);
        setTimeout(() => attachTitleHandlers(gd), 300);
      };
      gd.on('plotly_afterplot', onAfterPlot);
      // Also attach immediately if plot is already rendered
      setTimeout(() => attachTitleHandlers(gd), 200);
      
      (gd as any)._lumihandlers = { onClick, onSel, onDesel, onLegendClick, onAfterPlot };
    } catch (e) {
      console.warn('Failed to attach plotly_click handler', e);
    }
  };

  const detachPlotlyHandlers = (gd: any) => {
    try {
      const h = (gd as any)?._lumihandlers;
      if (!gd || !h) return;
      gd.removeListener?.('plotly_click', h.onClick);
      gd.removeListener?.('plotly_selected', h.onSel);
      gd.removeListener?.('plotly_deselect', h.onDesel);
      gd.removeListener?.('plotly_legendclick', h.onLegendClick);
      gd.removeListener?.('plotly_legenddoubleclick', h.onLegendClick);
      gd.removeListener?.('plotly_afterplot', h.onAfterPlot);
      
      // Clean up title handlers (axis titles and plot title)
      attachTitleHandlers(gd); // This will clean up existing handlers
      
      delete (gd as any)._lumihandlers;
    } catch {}
  };

  // Attach handlers by querying the DOM within this component
  useEffect(() => {
    const el = containerRef.current?.querySelector('.js-plotly-plot') as any;
    if (el) attachPlotlyHandlers(el);
    return () => {
      if (el) detachPlotlyHandlers(el);
    };
  }, [plotlyLayout?.editrevision]);

  return (
    <div className="flex-1 flex flex-col" data-testid="plot-canvas">
      {/* Canvas Toolbar (fixed height for alignment) */}
      <div className="bg-card border-b border-border h-12 px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === 'plot' ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab('plot')}
              data-testid="button-plot-view"
            >
              <Eye className="mr-2 h-4 w-4" />
              <span className="text-base font-medium">Plot</span>
            </Button>
            <Button
              variant={activeTab === 'data' ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab('data')}
              data-testid="button-data-view"
            >
              <Database className="mr-2 h-4 w-4" />
              <span className="text-base font-medium">Data</span>
            </Button>
            <Button
              variant={activeTab === 'code' ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab('code')}
              data-testid="button-code-view"
            >
              <Code className="mr-2 h-4 w-4" />
              <span className="text-base font-medium">Code</span>
            </Button>
          </div>
          
        </div>
        
        <div className="flex items-center gap-2">
          <TooltipProvider>
            {unsaved ? (
              <div className="text-xs text-amber-600 ml-2" data-testid="unsaved-indicator">{unsavedLabel || 'Unsaved changes'}</div>
            ) : null}
            {showFirstSaveNote && (
              <div className="text-xs text-muted-foreground ml-2 whitespace-nowrap" data-testid="first-save-note">
                Click the new plot button to start a new plot.
              </div>
            )}
            {/* Save */}
            {/* Wrap button in span to ensure tooltip works when disabled */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
            <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSave}
                    disabled={isSaving || isCodeMissing}
                  aria-busy={isSaving}
                  data-testid="button-save"
                  data-lumi="btn-save"
                    aria-disabled={isCodeMissing}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isCodeMissing 
                    ? 'Save will be available once all artifacts finish generating.' 
                    : isSaving 
                      ? 'Saving...' 
                      : 'Save and update the gallery'
                  }
                </p>
              </TooltipContent>
            </Tooltip>

            {/* Start New Plot */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="button-start-plot"
                  onClick={async () => {
                    if (isStarting) return;
                    
                    // Check dirty state: if current draft has unsaved edits, show confirmation dialog
                    if (isDirty) {
                      // Use the same dialog pattern as chat send
                      const shouldDiscard = window.confirm('You have unsaved changes. Click OK to discard and start a new plot, or Cancel to stay.');
                      if (!shouldDiscard) {
                        return;
                      }
                      // Optionally offer save: for now, just discard
                      // In the future, we could show a proper dialog with Save/Discard/Cancel options
                    }
                    
                    setIsStarting(true);
                    // Dismiss the first save note when user clicks new plot button
                    setShowFirstSaveNote(false);
                    if (firstSaveNoteTimeoutRef.current) {
                      window.clearTimeout(firstSaveNoteTimeoutRef.current);
                      firstSaveNoteTimeoutRef.current = null;
                    }
                    try {
                      const fn = (window as any).__lumiEnsureBoundDraft;
                      if (typeof fn === 'function') {
                        // Clear dataset hints prior to rebinding so the new session doesn't inherit them
                        try {
                          datasetInfoRef.current = null;
                          const pid = (() => { try { const m = window.location.pathname.match(/plot-editor\/(.+?)(?:\/|$)/); return m?.[1]; } catch { return undefined; } })();
                          if (pid) {
                            localStorage.removeItem(`lumiplot:project:${pid}:lastDatasetName`);
                            localStorage.removeItem(`lumiplot:project:${pid}:lastDatasetFileId`);
                          }
                        } catch {}
                        // Force a clean reset when clicking "new plot" button
                        // This ensures plot canvas, data tab, and code tab are all cleared
                        await fn(true);
                      } else {
                        try {
                          const el = document.querySelector('[data-testid=\"input-chat\"]') as HTMLTextAreaElement | null;
                          if (el) el.focus();
                        } catch {}
                      }
                    } finally {
                      setIsStarting(false);
                    }
                  }}
                  disabled={isStarting}
                  aria-busy={isStarting}
                  title="Start a new plot"
                >
                  {isStarting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LineChart className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isStarting ? 'Starting…' : 'Start a new plot'}</p>
              </TooltipContent>
            </Tooltip>
            
            {/* Export */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setExportModalOpen(true)}
                  data-testid="button-export"
                  data-lumi="btn-download"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export</p>
              </TooltipContent>
            </Tooltip>
            
            {/* Add to Figure */}
            {onAddToFigure && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => {
                      // Track CTA click for onboarding telemetry
                      try {
                        import('@/utils/telemetry').then(({ trackCTAClick }) => {
                          trackCTAClick('add_multipanel');
                        });
                      } catch {}
                      onAddToFigure();
                    }}
                    data-testid="button-add-to-figure"
                    data-lumi="btn-add-multipanel"
                  >
                    <FileImage className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add to Multi-panel Figure</p>
                </TooltipContent>
              </Tooltip>
            )}
            
            {/* Share */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={async () => {
                      // Track CTA click for onboarding telemetry
                      try {
                        const { trackCTAClick } = await import('@/utils/telemetry');
                        trackCTAClick('share');
                      } catch {}
                      handleShare();
                    }}
                    disabled={!currentPlot || !(currentPlot as any)?.latestVersionId}
                    data-testid="button-share"
                    data-lumi="btn-share-link"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!currentPlot || !(currentPlot as any)?.latestVersionId 
                    ? "Save your plot first to generate a share link" 
                    : "Share this plot with others"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Conflict notice below toolbar (does not affect toolbar height) */}
      {conflict ? (
        <div className="px-4 py-2 text-xs flex items-center gap-2 border-b border-border bg-amber-50 dark:bg-amber-950/20">
          <span className="text-amber-700 dark:text-amber-400 font-medium">Concurrent save detected. Another save is in progress.</span>
          <Button variant="secondary" size="sm" onClick={reloadLatestFromServer} data-testid="button-conflict-reload">Reload latest</Button>
          <Button variant="destructive" size="sm" onClick={overwriteAnyway} data-testid="button-conflict-overwrite">Overwrite anyway</Button>
        </div>
      ) : null}
      
      {/* Plot Canvas Content */}
      <div className="flex-1 p-6 overflow-auto">
        {activeTab === 'data' ? (
          <DataTab />
        ) : activeTab === 'code' ? (
          <CodeTab codeHook={codeHook} />
        ) : (
          <div 
            className="h-full plot-container rounded-lg flex items-center justify-center"
            data-testid="plot-container"
            ref={containerRef}
          >
            {currentPlot ? (
              <div className="w-full h-full">
                {(() => {
                  const pref = (plotlyLayout as any)?._previewScale;
                  const s = typeof pref === 'number' ? pref : autoScale;
                  const w = typeof (plotlyLayout as any)?.width === 'number' ? Math.round((plotlyLayout as any).width * s) : undefined;
                  const h = typeof (plotlyLayout as any)?.height === 'number' ? Math.round((plotlyLayout as any).height * s) : undefined;
                  const boxStyle: React.CSSProperties = {
                    width: w ? `${w}px` : '100%',
                    height: h ? `${h}px` : '100%'
                  };
                  return (
                    <div style={boxStyle}>
                  <Plot
                  data={plotlyData as any}
                  frames={plotlyFrames}
                  layout={(function(){
                    const L:any = withPlotTypography({ ...plotlyLayout });
                    // Apply editor-only preview scale by inflating layout width/height
                    const pref = (plotlyLayout as any)?._previewScale;
                    const renderScale = typeof pref === 'number' ? pref : autoScale;
                    if (typeof L.width === 'number') L.width = Math.round(L.width * renderScale);
                    if (typeof L.height === 'number') L.height = Math.round(L.height * renderScale);

                    // Preview-only font scaling (do not mutate original layout)
                    const baseFontSize = (plotlyLayout as any)?.font?.size ?? 10;
                    const scaleFont = (size?: number) => Math.max(1, Math.round(((size ?? baseFontSize) as number) * renderScale));

                    if ((plotlyLayout as any)?.font) {
                      L.font = { ...(L as any).font, size: scaleFont((L as any).font?.size) };
                    }
                    
                    // Scale title and subtitle fonts
                    if ((L as any)?.title) {
                      const title:any = { ...(L as any).title };
                      if (title.font) {
                        title.font = { ...title.font, size: scaleFont(title.font.size) };
                      }
                      // Handle subtitle (can be string or object with font)
                      if (title.subtitle) {
                        if (typeof title.subtitle === 'object' && title.subtitle !== null && title.subtitle.font) {
                          title.subtitle = {
                            ...title.subtitle,
                            font: { ...title.subtitle.font, size: scaleFont(title.subtitle.font.size) }
                          };
                        }
                        // If subtitle is a string, it will use the base font scaling which is already handled
                      }
                      L.title = title;
                    }
                    
                    const scaleAxisFonts = (axisKey: 'xaxis'|'yaxis') => {
                      if ((L as any)?.[axisKey]) {
                        const ax:any = (L as any)[axisKey];
                        const next:any = { ...ax };
                        if (ax.tickfont) next.tickfont = { ...ax.tickfont, size: scaleFont(ax.tickfont.size) };
                        if (ax.title) {
                          const tf:any = (ax.title as any).font ?? {};
                          next.title = { ...ax.title, font: { ...tf, size: scaleFont(tf.size) } };
                        }
                        L[axisKey] = next;
                      }
                    };
                    scaleAxisFonts('xaxis');
                    scaleAxisFonts('yaxis');

                    if ((L as any)?.legend) {
                      const lf:any = (L as any).legend.font ?? {};
                      L.legend = { ...(L as any).legend, font: { ...lf, size: scaleFont(lf.size) } };
                    }
                    if (Array.isArray((L as any)?.annotations)) {
                      L.annotations = (L as any).annotations.map((a:any) => {
                        const af:any = a?.font ?? {};
                        return { ...a, font: { ...af, size: scaleFont(af.size) } };
                      });
                    }
                    // Check for text labels in traces or annotations that might extend beyond plot area
                    const hasTextLabels = Array.isArray(plotlyData) && plotlyData.some((trace: any) => {
                      // Check if trace has text labels visible (mode includes "text" or textposition is set)
                      const mode = trace?.mode || '';
                      const hasTextMode = typeof mode === 'string' && mode.includes('text');
                      const hasTextPosition = trace?.textposition !== undefined;
                      const hasTextArray = Array.isArray(trace?.text) && trace.text.length > 0;
                      return hasTextMode || hasTextPosition || hasTextArray;
                    });
                    
                    // Also check for annotations positioned to the right (xref="x" with xanchor="right" or positioned near max x)
                    const hasRightAnnotations = Array.isArray(L?.annotations) && L.annotations.some((ann: any) => {
                      if (!ann?.text || typeof ann.text !== 'string') return false;
                      // Check if annotation uses x-axis coordinates and might be positioned to the right
                      return ann.xref === 'x' && (ann.xanchor === 'right' || ann.xanchor === 'left');
                    });

                    // Ensure adequate margins when text labels or right-positioned annotations are present
                    if ((hasTextLabels || hasRightAnnotations) && (!L.margin || typeof L.margin.r !== 'number' || L.margin.r < 80)) {
                      if (!L.margin) L.margin = {};
                      // Increase right margin to accommodate text labels that may extend right
                      if (typeof L.margin.r !== 'number' || L.margin.r < 80) {
                        L.margin.r = Math.max(80, L.margin.r || 80);
                      }
                    }

                    if (L?.width || L?.height) {
                      L.autosize = false;
                    } else {
                      L.autosize = true;
                      // When autosize is true, ensure margins are preserved (Plotly may override them)
                      // Set minimum margins to prevent label clipping
                      if (!L.margin) L.margin = {};
                      if (typeof L.margin.r !== 'number' || L.margin.r < 40) {
                        L.margin.r = Math.max(40, L.margin.r || 40);
                      }
                    }
                    // Favor selection by default so point picking works reliably
                    if (!L.dragmode) L.dragmode = 'select';
                    if (!L.selectdirection) L.selectdirection = 'any';
                    // Ensure Plotly emits click events and toggles selection on click
                    L.clickmode = 'event+select';
                    if (!L.hovermode || L.hovermode === false) {
                      L.hovermode = 'closest';
                    }
                    // Persist selection visuals across restyles
                    if (typeof selectionRev === 'number') {
                      L.selectionrevision = selectionRev;
                    }
                    return L;
                  })()}
                  revision={(plotlyLayout as any)?.editrevision || 0}
                  datarevision={(plotlyLayout as any)?.editrevision || 0}
                  config={{
                    displayModeBar: true,
                    // Keep selection tools available for point selection
                    modeBarButtonsToRemove: [],
                    displaylogo: false,
                    responsive: true,
                    editable: true,
                    // Mapbox access token for scattermapbox, choroplethmapbox, densitymapbox
                    // Even open-street-map style may require a token in some Plotly versions
                    mapboxAccessToken: getMapboxAccessToken(),
                    edits: {
                      annotationPosition: true,
                      annotationTail: true,
                      annotationText: false, // Don't allow inline text edit, use our controls
                      axisTitleText: true,
                      colorbarPosition: true,
                      colorbarTitleText: true,
                      legendPosition: true,
                      legendText: true,
                      shapePosition: true,
                      titleText: true
                    }
                  }}
                  style={{ width: '100%', height: '100%' }}
                  useResizeHandler={true}
                  onClick={(ev: any) => { ev?.event?.stopPropagation?.(); handlePlotlyClick(ev); }}
                  onRelayout={handlePlotlyRelayout as any}
                  onSelected={handlePlotlySelected as any}
                  onDeselect={handlePlotlyDeselect as any}
                  onInitialized={(_figure: any, gd: any) => {
                    graphDivRef.current = gd && gd.on ? gd : (containerRef.current?.querySelector('.js-plotly-plot') as any);
                    attachPlotlyHandlers(graphDivRef.current);
                  }}
                  onUpdate={(_figure: any, gd: any) => {
                    graphDivRef.current = gd && gd.on ? gd : (containerRef.current?.querySelector('.js-plotly-plot') as any);
                    attachPlotlyHandlers(graphDivRef.current);
                  }}
                      />
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center">
                <ChartLine className="h-10 w-10 text-muted-foreground opacity-80 mx-auto mb-2" />
                <h3 className="text-base font-medium text-muted-foreground mb-1">No plot created yet</h3>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Export Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Plot</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Format</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExport("png")}
                  data-testid="button-export-png"
                >
                  PNG
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport("svg")}
                  data-testid="button-export-svg"
                >
                  SVG
                </Button>
              </div>
            </div>
            
            <div className="bg-secondary rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <Code className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Python Code</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Get the reproducible code for this visualization
              </p>
              <Button
                variant="outline"
                size="sm"
                data-state={copiedCode ? 'copied' : 'idle'}
                onClick={() => handleExport("code")}
                data-testid="button-export-code"
              >
                {copiedCode ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </>
                )}
                <span className="sr-only" aria-live="polite">{copiedCode ? 'Copied to clipboard' : ''}</span>
              </Button>
            </div>

              <div className="bg-secondary rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Export Pack (ZIP)</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Download plotly.json, scripts, thumbnail, and a human‑readable summary.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPack}
                  data-testid="button-export-pack"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download ZIP
                  <Badge variant="secondary" className="ml-2">Soon</Badge>
                </Button>
              </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setExportModalOpen(false)}
              data-testid="button-cancel-export"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Plot</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Share Link</Label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/share/${shareToken}`}
                  readOnly
                  className="flex-1 bg-input border border-border rounded px-3 py-2 text-sm"
                  data-testid="input-share-url"
                />
                <Button
                  variant="secondary"
                  data-state={copiedShare ? 'copied' : 'idle'}
                  onClick={copyShareLink}
                  data-testid="button-copy-share-link"
                >
                  {copiedShare ? (
                    <>
                      <Check className="h-4 w-4 mr-1 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    'Copy'
                  )}
                  <span className="sr-only" aria-live="polite">{copiedShare ? 'Share link copied' : ''}</span>
                </Button>
              </div>
            </div>
            
            <div className="bg-muted rounded p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Link Details</span>
              </div>
              <ul className="text-muted-foreground space-y-1 text-xs">
                <li>• Read-only interactive view</li>
                <li>• Expires in 7 days</li>
                <li>• No download access</li>
                <li>• Can be revoked anytime</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setShareModalOpen(false)}
              data-testid="button-close-share"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
